import {
  getCachedStoresData,
  sortStoresByDistance,
  formatDistanceKm,
  buildWhatsappLink,
  buildDirectionsLink,
} from '@theme/zrzs-quick-add-stores';

/**
 * Fluxo de seleção de lente dentro do zrzs-quick-add-overlay. Busca as
 * páginas já renderizadas pela PDP (blocks/lens-selection.liquid) e
 * transplanta pro overlay, sem duplicar a lógica em Liquid. Classes
 * .lens-selection__* herdam o estilo de assets/lens-selection.css.
 */

/**
 * @typedef {object} LensFlowStep
 * @property {HTMLElement} page
 * @property {string} title
 * @property {string} subtitle
 * @property {string} [footnote]
 * @property {boolean} [custom] etapa com interação própria em vez de opções clicáveis.
 * @property {boolean} [rememberSelection] default true; false não restaura seleção anterior no DOM cacheado.
 * @property {boolean} [showSkip]
 * @property {boolean} [hideFooter]
 * @property {boolean} [hideStepCounter] ver setStepCounterText para contagem própria.
 * @property {boolean} [hideHeaderChrome] esconde título/subtítulo (usado só na etapa de permissão
 *   de localização) — não afeta o botão de voltar, ver hideBackButton.
 * @property {boolean} [hideBackButton] default false: botão de voltar some só quando true
 *   (nenhuma etapa usa hoje, inclusive a de permissão de localização mostra o dela).
 * @property {boolean} [lightContent] fundo branco em vez do cinza padrão — receita/resumo (Figma), nunca nas etapas de seleção (cards brancos sobre cinza).
 * @property {string} [continueLabel] default "continuar".
 * @property {boolean} [requireContinueButton] variante "com botão" de uma etapa de opções (não
 *   custom): default false/ausente avança com 1 clique na opção e esconde o rodapé (variante sem
 *   botão). true volta pro padrão antigo — rodapé visível, exige clique em "continuar" — sem mexer
 *   em onSelect/onContinue. Ver wireSelectableOption/render.
 * @property {boolean} [isMultifocal] marca a sub-pilha do fluxo multifocal (ver isInMultifocalFlow).
 * @property {(option: HTMLElement) => void} [onSelect]
 * @property {() => void} [onRender]
 * @property {() => void} [onContinue]
 * @property {() => void} [onSkip]
 * @property {object} [stateSnapshot] ver captureSelectionState.
 */

/**
 * @param {string} value
 * @returns {string}
 */
function formatCepInput(value) {
  const digits = value.replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 5) return digits;
  return `${digits.slice(0, 5)}-${digits.slice(5)}`;
}

/**
 * Ignora acento/caixa — ViaCEP e o metafield de lojas não usam
 * necessariamente a mesma capitalização.
 * @param {string | null | undefined} name
 * @returns {string}
 */
function normalizeCityName(name) {
  return (name || '')
    .toString()
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');
}

/**
 * Chamada direta do navegador pra ViaCEP. Só devolve a cidade
 * (`localidade`), sem distância.
 * @param {string} digits 8 dígitos, sem máscara.
 * @returns {Promise<string | null>}
 */
async function lookupCepCity(digits) {
  try {
    const response = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
    if (!response.ok) return null;
    const data = await response.json();
    if (data.erro) return null;
    return data.localidade || null;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[QuickAddLensFlow] Erro ao buscar CEP:', error);
    return null;
  }
}

/**
 * data-gifts vem em pseudo-JSON de aspas simples (mesmo parser de
 * zrzs-quick-add-trigger.js/zrzs-quick-add-pdp.js — duplicado aqui pelo
 * mesmo motivo: evitar mais um import relativo por 3 linhas).
 * @param {string | null | undefined} raw
 * @returns {any[] | null}
 */
function parseGiftsDataset(raw) {
  if (!raw) return null;
  try {
    return JSON.parse(raw.replace(/'/g, '"'));
  } catch {
    return null;
  }
}

/**
 * @param {string} productUrl
 * @returns {Promise<{
 *   page1: HTMLElement | null,
 *   page2: HTMLElement | null,
 *   page3: HTMLElement | null,
 *   upsellPage: HTMLElement | null,
 *   giftsData: any[] | null,
 * } | null>}
 */
export async function fetchLensPages(productUrl) {
  try {
    // PDP com cesta de lentes abre o fluxo pra si mesma (ver
    // zrzs-quick-add-pdp.js) — buscar a própria página de novo por rede
    // é puro custo (download + reparse do HTML inteiro) pra pegar algo
    // que já está carregado. document.cloneNode(true) é um clone nativo,
    // inerte (scripts não rodam fora de um documento ativo, igual o
    // DOMParser) e síncrono — mesma forma de uso abaixo, sem fetch.
    let doc;
    let samePage = false;
    try {
      samePage =
        new URL(productUrl, window.location.origin).pathname ===
        window.location.pathname;
    } catch {
      samePage = false;
    }

    if (samePage) {
      doc = /** @type {Document} */ (document.cloneNode(true));
    } else {
      const response = await fetch(productUrl);
      if (!response.ok) return null;
      const html = await response.text();
      doc = new DOMParser().parseFromString(html, 'text/html');
    }
    return {
      page1: doc.querySelector('.lens-selection__page--1'),
      page2: doc.querySelector('.lens-selection__page--2'),
      page3: doc.querySelector('.lens-selection__page--3'),
      upsellPage: doc.querySelector('.lens-selection__page--upsell'),
      // <lens-selection-component data-gifts="..."> — mesmo dado que o
      // trigger/PDP já leem, precisamos dele de novo no grau retroativo
      // pra não perder a elegibilidade de brinde da armação (ver
      // LensFlowController#advanceBatchFrame).
      giftsData: parseGiftsDataset(
        doc
          .querySelector('lens-selection-component')
          ?.getAttribute('data-gifts')
      ),
    };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(
      '[QuickAddLensFlow] Erro ao buscar página do produto:',
      error
    );
    return null;
  }
}

/**
 * Rodapé de 1 linha vinculado a uma opção específica (ex.: aviso de
 * multifocal, limite de grau incluso) — mesmo estilo nas duas.
 * @param {string} text
 * @returns {HTMLParagraphElement}
 */
function createOptionNote(text) {
  const note = document.createElement('p');
  note.className = 'zrzs-quick-add-lens-flow__option-note';
  note.textContent = text;
  return note;
}

/**
 * Duplica a opção "com grau" da página 1 em monofocal e multifocal —
 * multifocal não tem faixa de grau própria (ver advanceFromPage1).
 * @param {HTMLElement} page1
 */
export function splitMonofocalMultifocal(page1) {
  const original = page1.querySelector(
    '.lens-selection__option[data-has-children="true"]'
  );
  // Página cacheada: evita duplicar o sufixo ao reabrir.
  if (!(original instanceof HTMLElement) || original.dataset.zqaSplit) {
    return;
  }
  original.dataset.zqaSplit = 'true';

  const originalButton = original.querySelector(
    '.lens-selection__option-button'
  );
  const baseTitle = originalButton?.textContent?.trim() || '';
  if (originalButton) {
    originalButton.textContent = `${baseTitle} | longe ou perto`;
  }

  const clone = /** @type {HTMLElement} */ (original.cloneNode(true));
  clone.dataset.multifocal = 'true';
  const cloneButton = clone.querySelector('.lens-selection__option-button');
  if (cloneButton) {
    cloneButton.textContent = `${baseTitle} | multifocal`;
  }
  // Sem fonte própria pra descrição do multifocal ainda.
  const cloneDescription = clone.querySelector(
    '.lens-selection__option-description'
  );
  if (cloneDescription) {
    cloneDescription.textContent = 'grau para perto e longe no mesmo óculos.';
  }

  const divider = document.createElement('div');
  divider.className = 'lens-selection__option-divider';
  const note = createOptionNote(
    'lentes multifocais, são feitas presencialmente, veja a zerezes mais próxima*'
  );

  // `.lens-selection__options.reverse` (blocks/lens-selection.liquid, ligado
  // ao product_type == 'oculos-de-sol') inverte a ordem VISUAL via CSS sem
  // tocar o DOM — então "depois" no DOM visualmente vira "antes" nessa
  // cesta. Monta a sequência ao contrário nesse caso, pra qualquer cesta
  // (solar ou não) mostrar sempre monofocal → multifocal → nota, na tela.
  const isReversed = Boolean(
    original.closest('.lens-selection__options.reverse')
  );
  if (isReversed) {
    original.insertAdjacentElement('beforebegin', divider);
    divider.insertAdjacentElement('beforebegin', clone);
    clone.insertAdjacentElement('beforebegin', note);
  } else {
    original.insertAdjacentElement('afterend', divider);
    divider.insertAdjacentElement('afterend', clone);
    clone.insertAdjacentElement('afterend', note);
  }
}

/**
 * Opções sem custo adicional: o legado mostra "grátis", o Figma desse
 * fluxo pede "incluso" — em page2 (grau) com um aviso de limite logo
 * abaixo da opção; em page3 (coating, ex. filtro de luz azul) só o texto.
 * @param {HTMLElement | null | undefined} page
 * @param {{ note?: string }} [options]
 */
export function relabelFreePriceOptions(page, { note: noteText } = {}) {
  if (!page) return;
  page
    .querySelectorAll('.lens-selection__option-price--free')
    .forEach(priceEl => {
      priceEl.textContent = 'incluso';
      if (!noteText) return;
      const option = priceEl.closest('.lens-selection__option');
      if (!(option instanceof HTMLElement) || option.dataset.zqaFreeNote) {
        return;
      }
      option.dataset.zqaFreeNote = 'true';
      option.insertAdjacentElement('afterend', createOptionNote(noteText));
    });
}

/**
 * Mesmo selo de preço da página de grau (+R$ X / grátis), que o legado não
 * mostra pra essa opção na página 1. `frame_only` não tem data-price (sem
 * metacampo de preço — nunca é um SKU à parte), então cai sempre no texto
 * de "sem custo" passado em freeText.
 * @param {HTMLElement} option
 * @param {{ freeText?: string }} [options]
 */
function addPriceBadge(option, { freeText = 'grátis' } = {}) {
  const button = option.querySelector('.lens-selection__option-button');
  if (!(button instanceof HTMLElement)) return;
  const content = document.createElement('div');
  content.className = 'lens-selection__option-content';
  button.insertAdjacentElement('beforebegin', content);
  content.appendChild(button);
  const priceCents = priceCentsFromOption(option);
  const price = document.createElement('span');
  price.className = 'lens-selection__option-price';
  if (priceCents === 0) {
    price.classList.add('lens-selection__option-price--free');
    price.textContent = freeText;
  } else {
    price.textContent = `+${formatMoney(priceCents)}`;
  }
  content.appendChild(price);
}

/**
 * Junta `frame_only` e `non_prescription` (as 2 opções "sem grau" da
 * página 1) numa única opção "óculos sem grau" com sub-etapa. Os originais
 * só são escondidos (display:none + data-inert), não removidos, porque
 * resolveLensOptionInfo ainda precisa achá-los em page1 pra reconstruir o
 * detalhe de lente no mini-carrinho.
 * @param {HTMLElement} page1
 */
export function mergeFrameOnlyAndNonRx(page1) {
  const nonRxOption = page1.querySelector(
    '.lens-selection__option[data-has-children="false"][data-variant-ref]:not([data-frame-ref])'
  );
  const frameOnlyOption = page1.querySelector(
    '.lens-selection__option[data-frame-ref="true"]'
  );
  if (
    !(nonRxOption instanceof HTMLElement) ||
    !(frameOnlyOption instanceof HTMLElement) ||
    nonRxOption.dataset.inert
  ) {
    return;
  }

  const subPage = document.createElement('div');
  subPage.className = 'lens-selection__page zrzs-quick-add-sem-grau-group';
  const optionsWrap = document.createElement('div');
  optionsWrap.className = 'lens-selection__options';
  subPage.appendChild(optionsWrap);

  const frameOnlyClone = /** @type {HTMLElement} */ (
    frameOnlyOption.cloneNode(true)
  );
  delete frameOnlyClone.dataset.zqaWired;
  addPriceBadge(frameOnlyClone, { freeText: 'incluso' });
  const nonRxClone = /** @type {HTMLElement} */ (nonRxOption.cloneNode(true));
  delete nonRxClone.dataset.zqaWired;
  addPriceBadge(nonRxClone);
  optionsWrap.appendChild(frameOnlyClone);
  const innerDivider = document.createElement('div');
  innerDivider.className = 'lens-selection__option-divider';
  optionsWrap.appendChild(innerDivider);
  optionsWrap.appendChild(nonRxClone);

  const groupOption = document.createElement('div');
  groupOption.className = 'lens-selection__option';
  groupOption.dataset.semGrauGroup = 'true';
  /** @type {any} */ (groupOption).semGrauSubPage = subPage;
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'lens-selection__option-button';
  button.textContent = 'óculos sem grau';
  groupOption.appendChild(button);
  const description = document.createElement('p');
  description.className = 'lens-selection__option-description';
  description.textContent =
    'compre a armação zerezes sem grau e escolha entre lentes simples ou com filtro de luz azul para proteger seus olhos enquanto mantém o estilo.';
  groupOption.appendChild(description);

  nonRxOption.insertAdjacentElement('beforebegin', groupOption);

  const betweenDivider = nonRxOption.nextElementSibling;
  nonRxOption.dataset.inert = 'true';
  nonRxOption.style.display = 'none';
  if (
    betweenDivider instanceof HTMLElement &&
    betweenDivider.classList.contains('lens-selection__option-divider')
  ) {
    betweenDivider.style.display = 'none';
  }
  frameOnlyOption.dataset.inert = 'true';
  frameOnlyOption.style.display = 'none';
}

/**
 * Página 3 traz opções de todas as faixas de grau juntas, marcadas com
 * `.lens-selection__option--grading-{index}` — filtra pela escolhida.
 * @param {HTMLElement} page3
 * @param {string} gradingIndex
 */
export function filterCoatingOptionsByGrading(page3, gradingIndex) {
  page3.querySelectorAll('.lens-selection__option').forEach(option => {
    const matches = option.classList.contains(
      `lens-selection__option--grading-${gradingIndex}`
    );
    /** @type {HTMLElement} */ (option).style.display = matches ? '' : 'none';
  });
}

/**
 * "indique minha lente pela receita" — injetado aqui, não existe em
 * blocks/lens-selection.liquid.
 * @param {HTMLElement | null | undefined} page2
 * @param {() => void} onClick
 */
export function injectGradingRecommendationLink(page2, onClick) {
  if (!page2 || page2.dataset.zqaRecommendLink) return;
  page2.dataset.zqaRecommendLink = 'true';

  const section = page2.querySelector('.lens-selection__section');
  if (!section) return;

  const link = document.createElement('button');
  link.type = 'button';
  link.className = 'zrzs-quick-add-lens-flow__recommend-link';
  link.innerHTML = `
    indique minha lente pela receita
    <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M3 8H13M13 8L9 4M13 8L9 12" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>
  `;
  link.addEventListener('click', onClick);
  section.appendChild(link);
}

// Mesmo número/convenção wa.me já usada em sections/side-menu.liquid e
// snippets/chat-cta.liquid — não existe uma settings global única pra
// esse contato, é hardcoded em todos os outros lugares do tema também.
const CONSULTANT_WHATSAPP_NUMBER = '552123919797';
const CONSULTANT_WHATSAPP_MESSAGE =
  'Oi, tudo bem? preciso de ajuda para escolher o grau das minhas lentes. ::)';

/**
 * Card "fale com um consultor" ao final da lista de opções de grau —
 * mesmo em ambos os fluxos (grau comum e retroativo em solar).
 * @param {HTMLElement | null | undefined} page2
 * @param {string} [whatsappNumber] admin (settings.quick_add_consultant_whatsapp_number) — ver LensFlowController#consultantWhatsappNumber.
 */
export function injectConsultantCard(
  page2,
  whatsappNumber = CONSULTANT_WHATSAPP_NUMBER
) {
  if (!page2 || page2.dataset.zqaConsultantCard) return;
  page2.dataset.zqaConsultantCard = 'true';

  const container = page2.querySelector('.lens-selection__options') || page2;

  const card = document.createElement('div');
  card.className = 'zrzs-quick-add-lens-flow__consultant-card';
  // Observado por assets/zrzs-business-hours.js (sitewide, lê a config de
  // horário/dias do bloco "new" do side menu) — some fora do horário de
  // atendimento, via CSS, sem esse arquivo precisar saber nada sobre
  // horário/dia/feriado (ver zrzs-quick-add.css).
  card.dataset.businessHoursWatch = '';
  card.innerHTML = `
    <p class="zrzs-quick-add-lens-flow__consultant-text">dúvidas? compre seu zerezes com um dos nossos consultores ::)</p>
    <a
      class="zrzs-quick-add-lens-flow__consultant-button"
      href="https://wa.me/${whatsappNumber}?text=${encodeURIComponent(CONSULTANT_WHATSAPP_MESSAGE)}"
      target="_blank"
      rel="noopener"
    >
      <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <path d="M15.875 4.09167C15.1109 3.32009 14.2009 2.70831 13.1979 2.29197C12.195 1.87563 11.1192 1.66307 10.0333 1.66667C5.48333 1.66667 1.775 5.375 1.775 9.925C1.775 11.3833 2.15833 12.8 2.875 14.05L1.70833 18.3333L6.08333 17.1833C7.29167 17.8417 8.65 18.1917 10.0333 18.1917C14.5833 18.1917 18.2917 14.4833 18.2917 9.93333C18.2917 7.725 17.4333 5.65 15.875 4.09167ZM10.0333 16.7917C8.8 16.7917 7.59167 16.4583 6.53333 15.8333L6.28333 15.6833L3.68333 16.3667L4.375 13.8333L4.20833 13.575C3.52296 12.4809 3.1591 11.2161 3.15833 9.925C3.15833 6.14167 6.24167 3.05833 10.025 3.05833C11.8583 3.05833 13.5833 3.775 14.875 5.075C15.5147 5.71155 16.0216 6.46879 16.3664 7.30277C16.7111 8.13676 16.8868 9.0309 16.8833 9.93333C16.9 13.7167 13.8167 16.7917 10.0333 16.7917ZM13.8 11.6583C13.5917 11.5583 12.575 11.0583 12.3917 10.9833C12.2 10.9167 12.0667 10.8833 11.925 11.0833C11.7833 11.2917 11.3917 11.7583 11.275 11.8917C11.1583 12.0333 11.0333 12.05 10.825 11.9417C10.6167 11.8417 9.95 11.6167 9.16667 10.9167C8.55 10.3667 8.14167 9.69167 8.01667 9.48333C7.9 9.275 8 9.16667 8.10833 9.05833C8.2 8.96667 8.31667 8.81667 8.41667 8.7C8.51667 8.58333 8.55833 8.49167 8.625 8.35833C8.69167 8.21667 8.65833 8.1 8.60833 8C8.55833 7.9 8.14167 6.88333 7.975 6.46667C7.80833 6.06667 7.63333 6.11667 7.50833 6.10833H7.10833C6.96667 6.10833 6.75 6.15833 6.55833 6.36667C6.375 6.575 5.84167 7.075 5.84167 8.09167C5.84167 9.10833 6.58333 10.0917 6.68333 10.225C6.78333 10.3667 8.14167 12.45 10.2083 13.3417C10.7 13.5583 11.0833 13.6833 11.3833 13.775C11.875 13.9333 12.325 13.9083 12.6833 13.8583C13.0833 13.8 13.9083 13.3583 14.075 12.875C14.25 12.3917 14.25 11.9833 14.1917 11.8917C14.1333 11.8 14.0083 11.7583 13.8 11.6583Z" fill="currentColor"/>
      </svg>
      entrar em contato
    </a>
  `;
  container.appendChild(card);
}

/**
 * Faixas de grau catalogadas por TÍTULO da grading_option, não por
 * posição — cestas diferentes têm faixas diferentes e o metaobjeto não
 * expõe valor numérico estruturado. Título desconhecido: cadastrar aqui.
 * @type {Record<string, { spherePositiveMax: number, sphereNegativeMax: number, cylinderMax: number }>}
 */
export const GRADING_RANGES_BY_TITLE = {
  'grau leve': {
    spherePositiveMax: 2.75,
    sphereNegativeMax: 2.75,
    cylinderMax: 2.0,
  },
  'grau moderado': {
    spherePositiveMax: 4.0,
    sphereNegativeMax: 4.0,
    cylinderMax: 2.0,
  },
  'grau alto': {
    spherePositiveMax: 5.0,
    sphereNegativeMax: 6.5,
    cylinderMax: 4.0,
  },
  'grau leve a moderado': {
    spherePositiveMax: 4.0,
    sphereNegativeMax: 4.0,
    cylinderMax: 2.0,
  },
};

/**
 * Maior faixa cadastrada em qualquer cesta — usado só pra limitar as opções
 * do dropdown de esférico/cilíndrico. Não precisa variar por lente
 * escolhida: upgrade de faixa + aviso (ver updateWarning) já cobre quem
 * escolher um grau acima da faixa atualmente selecionada.
 * @returns {{ spherePositiveMax: number, sphereNegativeMax: number, cylinderMax: number }}
 */
function getFullGradingRange() {
  const ranges = Object.values(GRADING_RANGES_BY_TITLE);
  return {
    spherePositiveMax: Math.max(...ranges.map(r => r.spherePositiveMax)),
    sphereNegativeMax: Math.max(...ranges.map(r => r.sphereNegativeMax)),
    cylinderMax: Math.max(...ranges.map(r => r.cylinderMax)),
  };
}

/**
 * @param {number} value múltiplo de 0,25
 * @returns {string} "2,25", "-1,5", "0" — vírgula decimal, igual ao resto do fluxo.
 */
function formatGradingStepValue(value) {
  const rounded = Math.round(value * 100) / 100;
  if (Number.isInteger(rounded)) return String(rounded);
  return rounded.toFixed(2).replace(/0$/, '').replace('.', ',');
}

/**
 * @param {number} min
 * @param {number} max
 * @returns {string[]} valores em passo de 0,25, do menor pro maior.
 */
function buildQuarterStepOptions(min, max) {
  const values = [];
  const steps = Math.round((max - min) / 0.25);
  for (let i = 0; i <= steps; i++) {
    values.push(formatGradingStepValue(min + i * 0.25));
  }
  return values;
}

const FULL_GRADING_RANGE = getFullGradingRange();
/** Opções do dropdown de esférico (OD/OE) — ver buildPrescriptionSection. */
const SPHERE_STEP_OPTIONS = buildQuarterStepOptions(
  -FULL_GRADING_RANGE.sphereNegativeMax,
  FULL_GRADING_RANGE.spherePositiveMax
);
/** Opções do dropdown de cilíndrico (OD/OE) — ver buildPrescriptionSection. */
const CYLINDER_STEP_OPTIONS = buildQuarterStepOptions(
  -FULL_GRADING_RANGE.cylinderMax,
  FULL_GRADING_RANGE.cylinderMax
);

/**
 * "Grau leve | até +/- 2,75" → "grau leve" (chave em GRADING_RANGES_BY_TITLE).
 * @param {string} rawTitle
 */
function gradingTitleKey(rawTitle) {
  return rawTitle.split('|')[0].trim().toLowerCase();
}

/** @type {Set<string>} evita logar o mesmo console.warn repetidas vezes por sessão. */
const warnedUnknownGradingTitles = new Set();

/**
 * @param {string} rawTitle texto completo do botão da opção (grading_option.title)
 * @returns {{ spherePositiveMax: number, sphereNegativeMax: number, cylinderMax: number } | null}
 */
function getGradingRangeForTitle(rawTitle) {
  const key = gradingTitleKey(rawTitle || '');
  const range = GRADING_RANGES_BY_TITLE[key];
  if (!range && key && !warnedUnknownGradingTitles.has(key)) {
    warnedUnknownGradingTitles.add(key);
    // eslint-disable-next-line no-console
    console.warn(
      `[QuickAddLensFlow] Faixa de grau desconhecida: "${key}" — não está em GRADING_RANGES_BY_TITLE, validação de limite pulada pra essa opção. Cadastrar o valor real.`
    );
  }
  return range || null;
}

/**
 * `data-price` já vem em centavos, resolvido pelo Liquid.
 * @param {HTMLElement} option
 */
function priceCentsFromOption(option) {
  const raw = option.dataset.price ? parseInt(option.dataset.price, 10) : 0;
  return Number.isNaN(raw) ? 0 : raw;
}

/**
 * @typedef {object} LensOptionInfo
 * @property {string | null} title
 * @property {number} priceCents
 * @property {string | null} variantRef
 * @property {string | null} productSlug
 * @property {string | null} variantTitle
 */

/**
 * Dados de uma opção "final" (grading sem coating, ou coating) —
 * equivalente ao `finalSelection` de lens-selection.js#addToCart.
 * @param {HTMLElement} option
 * @returns {LensOptionInfo}
 */
function captureLensOptionData(option) {
  return {
    title:
      option
        .querySelector('.lens-selection__option-button')
        ?.textContent?.trim() || null,
    priceCents: priceCentsFromOption(option),
    variantRef: option.dataset.variantRef || null,
    productSlug: option.dataset.productSlug || null,
    variantTitle: option.dataset.variantTitle || null,
  };
}

/**
 * data-price da página 3 é o preço ABSOLUTO já configurado, não um
 * acréscimo — vira delta sobre o preço-base da faixa de grau.
 * @param {LensOptionInfo} coatingData
 * @param {number} gradingBaseCents
 * @returns {LensOptionInfo}
 */
function toCoatingDelta(coatingData, gradingBaseCents) {
  return {
    ...coatingData,
    priceCents: Math.max(coatingData.priceCents - gradingBaseCents, 0),
  };
}

/**
 * @param {HTMLElement | null | undefined} page
 * @param {string} variantId
 * @returns {HTMLElement | null}
 */
function findOptionByVariantRef(page, variantId) {
  if (!page) return null;
  const options = /** @type {NodeListOf<HTMLElement>} */ (
    page.querySelectorAll('.lens-selection__option')
  );
  return (
    Array.from(options).find(
      option => option.dataset.variantRef === variantId
    ) || null
  );
}

/**
 * Opção de coating pra uma faixa de grau, preservando o acabamento já
 * escolhido quando existir; sem equivalente, cai pro sem acréscimo.
 * @param {HTMLElement} page3
 * @param {string} gradingIndex
 * @param {string | null} [preferredTitle]
 * @returns {HTMLElement | null}
 */
function findCoatingOptionForGrading(page3, gradingIndex, preferredTitle) {
  const options = Array.from(
    /** @type {NodeListOf<HTMLElement>} */ (
      page3.querySelectorAll('.lens-selection__option')
    )
  ).filter(option =>
    option.classList.contains(`lens-selection__option--grading-${gradingIndex}`)
  );
  if (!options.length) return null;
  if (preferredTitle) {
    const match = options.find(
      option =>
        option
          .querySelector('.lens-selection__option-button')
          ?.textContent?.trim() === preferredTitle
    );
    if (match) return match;
  }
  return options.reduce((cheapest, option) =>
    priceCentsFromOption(option) < priceCentsFromOption(cheapest)
      ? option
      : cheapest
  );
}

/**
 * Reconstrói grading+coating a partir do variant_id salvo no carrinho (`_lente`).
 * @param {{ page1: HTMLElement | null, page2: HTMLElement | null, page3: HTMLElement | null } | null} lensPages
 * @param {string | number} variantId
 * @returns {{ grading: LensOptionInfo | null, coating: LensOptionInfo | null }}
 */
export function resolveLensOptionInfo(lensPages, variantId) {
  const targetId = String(variantId);

  const coatingOption = findOptionByVariantRef(lensPages?.page3, targetId);
  if (coatingOption) {
    const gradingIndex = coatingOption.dataset.gradingIndex;
    const page2Options = /** @type {NodeListOf<HTMLElement>} */ (
      lensPages?.page2?.querySelectorAll('.lens-selection__option') || []
    );
    const gradingOption = gradingIndex
      ? Array.from(page2Options).find(
          option => option.dataset.gradingIndex === gradingIndex
        ) || null
      : null;
    return {
      grading: gradingOption ? captureLensOptionData(gradingOption) : null,
      coating: toCoatingDelta(
        captureLensOptionData(coatingOption),
        gradingOption ? priceCentsFromOption(gradingOption) : 0
      ),
    };
  }

  const gradingOption = findOptionByVariantRef(lensPages?.page2, targetId);
  if (gradingOption) {
    return { grading: captureLensOptionData(gradingOption), coating: null };
  }

  // Opção não-Rx com lente própria na página 1 (ex. "sem grau | com
  // filtro de luz azul") — sem faixa de grau nem coating, só a variante direto.
  const page1Option = findOptionByVariantRef(lensPages?.page1, targetId);
  if (page1Option) {
    return { grading: captureLensOptionData(page1Option), coating: null };
  }

  return { grading: null, coating: null };
}

const moneyWithCents = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
});
const moneyNoCents = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

/**
 * "R$ 645" sem centavos, "R$ 645,50" com.
 * @param {number} cents
 */
export function formatMoney(cents) {
  const value = (cents || 0) / 100;
  const hasFractionalCents = (cents || 0) % 100 !== 0;
  return (hasFractionalCents ? moneyWithCents : moneyNoCents).format(value);
}

/**
 * "esf -3,25 / cil -1,00 / eixo 170°".
 * @param {{ odSphere: string, odCylinder: string, odAxis: string, oeSphere: string, oeCylinder: string, oeAxis: string }} values
 * @param {'od' | 'oe'} eye
 */
export function formatPrescriptionSummaryLine(values, eye) {
  const sphere = values[`${eye}Sphere`] || '0';
  const cylinder = values[`${eye}Cylinder`] || '0';
  const axis = values[`${eye}Axis`] || '0';
  return `esf ${sphere} / cil ${cylinder} / eixo ${axis}°`;
}

/**
 * Linha "label à esquerda, valor à direita"; classes CSS a cargo de quem chama.
 * @param {string} label
 * @param {string} value
 * @param {{ row?: string, label?: string, value?: string }} [classNames]
 */
export function buildLabelValueRow(label, value, classNames = {}) {
  const row = document.createElement('div');
  if (classNames.row) row.className = classNames.row;
  const labelEl = document.createElement('p');
  if (classNames.label) labelEl.className = classNames.label;
  labelEl.textContent = label;
  const valueEl = document.createElement('p');
  if (classNames.value) valueEl.className = classNames.value;
  valueEl.textContent = value;
  row.append(labelEl, valueEl);
  return row;
}

/**
 * "-1,75" (formato BR da receita) → -1.75. NaN se vazio/inválido.
 * @param {string} raw
 */
function parseDecimal(raw) {
  if (!raw) return NaN;
  const normalized = raw.trim().replace(',', '.');
  return Number.parseFloat(normalized);
}

/**
 * Filtra esférico/cilíndrico pra dígitos + 1 separador decimal + sinal
 * de menos no início.
 * @param {string} raw
 */
function sanitizeDecimalInput(raw) {
  const negative = raw.trim().startsWith('-');
  let digits = raw.replace(/[^0-9,.]/g, '').replace(/\./g, ',');
  const firstComma = digits.indexOf(',');
  if (firstComma !== -1) {
    digits =
      digits.slice(0, firstComma + 1) +
      digits.slice(firstComma + 1).replace(/,/g, '');
  }
  return (negative ? '-' : '') + digits;
}

/**
 * Eixo é sempre 0-180° — filtra dígitos e satura em 180 durante a digitação.
 * @param {string} raw
 */
function sanitizeAxisInput(raw) {
  const digits = raw.replace(/[^0-9]/g, '');
  if (digits === '') return '';
  const value = Math.min(180, Number.parseInt(digits, 10));
  return String(value);
}

/** @param {string} raw */
function isValidDecimalValue(raw) {
  return raw.trim() !== '' && !Number.isNaN(parseDecimal(raw));
}

/** @param {string} raw */
function isValidAxisValue(raw) {
  if (raw.trim() === '') return false;
  const value = Number.parseInt(raw, 10);
  return !Number.isNaN(value) && value >= 0 && value <= 180;
}

/** Cilíndrico é opcional — vazio = sem astigmatismo. @param {string} raw */
function isValidOptionalDecimalValue(raw) {
  return raw.trim() === '' || !Number.isNaN(parseDecimal(raw));
}

/**
 * Eixo só é obrigatório quando o cilíndrico do MESMO olho tem valor
 * diferente de zero — sem astigmatismo (cilíndrico vazio ou 0), o eixo não
 * descreve nada e fica opcional também.
 * @param {string} axisRaw
 * @param {string} cylinderRaw
 */
function isValidAxisValueFor(axisRaw, cylinderRaw) {
  const cylinderValue = parseDecimal(cylinderRaw);
  const needsAxis = !Number.isNaN(cylinderValue) && cylinderValue !== 0;
  return needsAxis ? isValidAxisValue(axisRaw) : true;
}

/**
 * @param {number} sphere
 * @param {number} cylinder
 * @param {{ spherePositiveMax: number, sphereNegativeMax: number, cylinderMax: number }} range
 */
function fitsRange(sphere, cylinder, range) {
  const sphereOk = Number.isNaN(sphere)
    ? true
    : sphere >= 0
      ? sphere <= range.spherePositiveMax
      : Math.abs(sphere) <= range.sphereNegativeMax;
  const cylinderOk = Number.isNaN(cylinder)
    ? true
    : Math.abs(cylinder) <= range.cylinderMax;
  return sphereOk && cylinderOk;
}

/**
 * @param {{ odSphere: string, odCylinder: string, oeSphere: string, oeCylinder: string }} values
 * @param {{ spherePositiveMax: number, sphereNegativeMax: number, cylinderMax: number }} range
 */
function valuesFitRange(values, range) {
  return (
    fitsRange(
      parseDecimal(values.odSphere),
      parseDecimal(values.odCylinder),
      range
    ) &&
    fitsRange(
      parseDecimal(values.oeSphere),
      parseDecimal(values.oeCylinder),
      range
    )
  );
}

/**
 * Alguma cesta do site (não só a do produto atual) cobre esse grau? —
 * distingue "fora da faixa desse produto" (o site atende, só não essa
 * cesta) de "fora de tudo que o site atende" (ver updateWarning).
 * @param {{ odSphere: string, odCylinder: string, oeSphere: string, oeCylinder: string }} values
 */
function fitsAnyKnownGradingRange(values) {
  return Object.values(GRADING_RANGES_BY_TITLE).some(range =>
    valuesFitRange(values, range)
  );
}

/** @param {{ odSphere: string, odCylinder: string, oeSphere: string, oeCylinder: string }} values */
function hasAnyGradingValue(values) {
  return (
    !Number.isNaN(parseDecimal(values.odSphere)) ||
    !Number.isNaN(parseDecimal(values.odCylinder)) ||
    !Number.isNaN(parseDecimal(values.oeSphere)) ||
    !Number.isNaN(parseDecimal(values.oeCylinder))
  );
}

/**
 * Primeira opção da página 2 cuja faixa conhecida cobre os 4 valores.
 * @param {HTMLElement} page2
 * @param {{ odSphere: string, odCylinder: string, oeSphere: string, oeCylinder: string }} values
 * @returns {{ title: string, gradingIndex: string | undefined } | null}
 */
function findRequiredGradingOption(page2, values) {
  const options = page2.querySelectorAll('.lens-selection__option');
  for (const option of options) {
    const titleEl = option.querySelector('.lens-selection__option-button');
    const title = titleEl?.textContent?.trim() || '';
    const range = GRADING_RANGES_BY_TITLE[gradingTitleKey(title)];
    if (range && valuesFitRange(values, range)) {
      return { title, gradingIndex: option.dataset.gradingIndex };
    }
  }
  return null;
}

/**
 * Controla o painel "lens-flow" (pilha de etapas), isolado do resto do
 * overlay (confirmação, carrinho, cupom, frete).
 */
export class LensFlowController {
  /**
   * @param {{
   *   content: HTMLElement | null,
   *   header: HTMLElement | null,
   *   backButton: HTMLElement | null,
   *   title: HTMLElement | null,
   *   stepCounter: HTMLElement | null,
   *   subtitle: HTMLElement | null,
   *   continueButton: HTMLButtonElement | null,
   *   skipButton: HTMLButtonElement | null,
   *   footer: HTMLElement | null,
   *   popup: {
   *     root: HTMLElement | null,
   *     title: HTMLElement | null,
   *     body: HTMLElement | null,
   *     closeButton: HTMLElement | null,
   *     backButton: HTMLButtonElement | null,
   *     continueButton: HTMLButtonElement | null,
   *   },
   * }} refs
   * @param {{ onOpen: () => void, onExit: (message?: string) => void, onReopenAnimated?: () => void, onAddFrameAndShowCart: () => (Promise<boolean> | void), onAddConfiguredLensToCart: (config: { lens: object, prescription: object | null }) => Promise<boolean>, storesMapImageUrl?: string | null, consultantWhatsappNumber?: string | null }} callbacks
   *   onAddFrameAndShowCart: fluxo sem grau. onAddConfiguredLensToCart: roda o /cart/add.js do resumo.
   *   Ambos retornam `false` (via Promise) quando o /cart/add.js falha, pra exibir erro.
   *   onExit: `message` só vem de uma falha real de fetch (ver open()) — exibe erro em vez de sair calado.
   *   onReopenAnimated: replay da animação de abertura do overlay — só pra
   *   entradas específicas que devem parecer uma tela nova "chegando" (ver
   *   showExceedsMaxPopup), nunca em navegação normal entre etapas.
   *   storesMapImageUrl/consultantWhatsappNumber vêm do admin (settings_schema.json).
   */
  constructor(
    refs,
    {
      onOpen,
      onExit,
      onReopenAnimated,
      onAddFrameAndShowCart,
      onAddConfiguredLensToCart,
      storesMapImageUrl,
      consultantWhatsappNumber,
    }
  ) {
    this.refs = refs;
    this.storesMapImageUrl = storesMapImageUrl || null;
    this.consultantWhatsappNumber =
      consultantWhatsappNumber || CONSULTANT_WHATSAPP_NUMBER;
    this.onOpen = onOpen;
    this.onExit = onExit;
    this.onReopenAnimated = onReopenAnimated || (() => {});
    this.onAddFrameAndShowCart = onAddFrameAndShowCart;
    this.onAddConfiguredLensToCart = onAddConfiguredLensToCart;
    /** @type {{ page1: HTMLElement, page2: HTMLElement | null, page3: HTMLElement | null, upsellPage: HTMLElement | null } | null} */
    this.lensPages = null;
    /** @type {Promise<any> | null} promessa em voo do fetch de open() — reusada por chamadas concorrentes, nunca duplicada. */
    this.lensPagesPromise = null;
    /** @type {number} bump em toda open()/reset() — invalida uma open() antiga que resolve depois de um reset()/reopen mais novo já ter assumido. */
    this.openToken = 0;
    /** @type {Array<LensFlowStep>} */
    this.history = [];
    this.pendingSelection = null;
    /** @type {boolean} trava o clique de uma etapa sem botão (ver
     *  wireSelectableOption/render) enquanto o onSelect de uma opção que
     *  NÃO troca de etapa (ex.: "óculos sem grau" → /cart/add.js direto,
     *  sem passar pelo resumo) ainda está em voo — sem isso, clique duplo
     *  no card disparava 2 /cart/add.js (mesma classe de bug já relatada
     *  em QA). Resetada a cada render() (toda troca de etapa já destrava
     *  sozinha) e no finally do próprio onAddFrameAndShowCart direto (ver
     *  advanceFromPage1), pra permitir nova tentativa se a 1ª falhar. */
    this.oneClickLock = false;
    /** @type {{ image: string, title: string, price: string, priceCents: number } | null} */
    this.productSummary = null;
    /** @type {string | null} data-grading-index — só pra filtrar coating, validação usa selectedGradingTitle. */
    this.selectedGradingIndex = null;
    /** @type {string | null} título da faixa escolhida — chave em GRADING_RANGES_BY_TITLE. */
    this.selectedGradingTitle = null;
    /** @type {number} valor adicional da faixa (0 se grátis). */
    this.selectedGradingPriceCents = 0;
    /** @type {LensOptionInfo | null} coating escolhido na página 3, só quando existe. */
    this.selectedCoating = null;
    /** @type {LensOptionInfo | null} variante que vai pro carrinho como lente. */
    this.finalLensSelection = null;
    /** @type {{ odSphere: string, odCylinder: string, odAxis: string, oeSphere: string, oeCylinder: string, oeAxis: string } | null}
     *  Fonte única de verdade pra "já temos a receita" (ver advanceFromPage3 e buildPage2Step).
     *  Deliberadamente FORA de captureSelectionState — precisa sobreviver a
     *  goBack()/reavanço (ex.: voltar só pra trocar a proteção extra), senão
     *  o snapshot da etapa anterior (capturado antes da receita existir) a
     *  apagava a cada "voltar", mesmo já confirmada (bug real relatado em QA). */
    this.prescriptionValues = null;
    /** @type {string | null} título da faixa de grau vigente quando
     *  prescriptionValues foi confirmado — única forma estável de saber se
     *  a faixa realmente mudou depois (selectedGradingTitle É restaurado
     *  pelo snapshot, não serve como referência). Null quando prescriptionValues
     *  ainda não está atrelado a nenhuma faixa específica (ex.: acabou de
     *  confirmar via "indique minha lente pela receita", antes de escolher
     *  a faixa sugerida) — nesse caso a 1ª escolha após a receita nunca invalida. */
    this.prescriptionValuesGradingTitle = null;
    /** @type {{ odSphere: string, odCylinder: string, odAxis: string, oeSphere: string, oeCylinder: string, oeAxis: string } | null}
     *  O que está digitado AGORA, mesmo sem ter clicado "confirmar valores"
     *  — atualizado a cada tecla (ver buildPrescriptionStep). Também fora
     *  de captureSelectionState: sem isso, digitar a receita e reabrir essa
     *  etapa (voltar + trocar a proteção extra, por exemplo) sem nunca ter
     *  confirmado perdia tudo, já que prescriptionValues nem chega a
     *  existir nesse caso. */
    this.draftPrescriptionValues = null;
    /** @type {number | null} total fixo pro caminho "óculos sem grau"
     *  (mergeFrameOnlyAndNonRx) — a conta genérica de getStepTotal assume
     *  grau/coating/receita, que esse caminho nunca usa. */
    this.stepTotalOverride = null;
    /** @type {boolean} true após "indique minha lente pela receita" nesta
     *  passagem — incrementa getStepTotal em +1 (ação deliberada do
     *  usuário, diferente de só seguir em frente — ver handleRecommendClick). */
    this.recommendUsedInThisPass = false;
    /** @type {boolean} escolha feita em buildPrescriptionChoiceStep — true
     *  só quando "preencher receita agora" foi clicado nesta passagem,
     *  incrementa getStepTotal em +1 pra contar a etapa de receita (ver
     *  advanceFromPrescriptionChoice). "enviar depois" nunca passa por lá. */
    this.willFillPrescriptionNow = false;
    /** @type {number} quantas etapas ocultas (sem contador) já passaram
     *  antes da 1ª etapa visível desta sessão — 1 no fluxo normal (página 1
     *  nunca mostra contador, ver open()), 0 no retroativo (openBatch nunca
     *  passa pela página 1). Usado só pra exibição: subtrai de history.length
     *  e do total pra o contador sempre começar em "etapa 1" quando aparece
     *  pela 1ª vez, já com o caminho decidido. */
    this.stepCounterBase = 0;
    /** @type {string | null} data-grading-index destacado em verde na
     *  página 2 via "indique minha lente pela receita" — ver buildPage2Step. */
    this.highlightedGradingIndex = null;
    /** @type {Array<any> | null} fila do fluxo em lote (grau retroativo em
     *  solar, a partir do carrinho) — null fora dele. Ver openBatch. */
    this.batchQueue = null;
    /** @type {number} índice do frame atual em batchQueue. */
    this.batchIndex = 0;
    /** @type {Array<any>} */
    this.batchResults = [];
    /** @type {((results: Array<any>) => Promise<boolean> | void) | null} */
    this.batchOnComplete = null;

    if (this.refs.backButton) {
      this.refs.backButton.addEventListener('click', () => this.goBack());
    }
    if (this.refs.continueButton) {
      this.refs.continueButton.addEventListener('click', () =>
        this.handleContinueClick()
      );
    }
    if (this.refs.skipButton) {
      this.refs.skipButton.addEventListener('click', () =>
        this.handleSkipClick()
      );
    }

    const popup = this.refs.popup;
    if (popup?.closeButton) {
      popup.closeButton.addEventListener('click', () => this.hidePopup());
    }
    if (popup?.backButton) {
      popup.backButton.addEventListener('click', () => this.hidePopup());
    }
  }

  /** Chamado toda vez que um novo item entra no painel de confirmação. */
  reset() {
    this.lensPages = null;
    this.lensPagesPromise = null;
    this.openToken += 1;
    this.history = [];
    this.pendingSelection = null;
    this.oneClickLock = false;
    this.productSummary = null;
    this.selectedGradingIndex = null;
    this.selectedGradingTitle = null;
    this.selectedGradingPriceCents = 0;
    this.selectedCoating = null;
    this.finalLensSelection = null;
    this.prescriptionValues = null;
    this.prescriptionValuesGradingTitle = null;
    this.draftPrescriptionValues = null;
    this.stepTotalOverride = null;
    this.recommendUsedInThisPass = false;
    this.willFillPrescriptionNow = false;
    this.stepCounterBase = 0;
    this.highlightedGradingIndex = null;
    this.batchQueue = null;
    this.batchIndex = 0;
    this.batchResults = [];
    this.batchOnComplete = null;
  }

  /**
   * Invalida um open()/advanceBatchFrame() ainda em voo (fetch não
   * resolvido), sem mexer no resto do estado — diferente de reset(), que
   * também apaga seleções/histórico. Chamado quando o overlay fecha
   * enquanto a página de lentes ainda carrega (ver close() em
   * zrzs-quick-add-overlay.js): sem isso, o fetch resolve depois e empurra
   * a página 1 por cima de um painel que o usuário já fechou.
   */
  invalidatePendingOpen() {
    this.openToken += 1;
  }

  /**
   * Busca (uma vez por abertura) a página do produto e mostra a página 1.
   * @param {string} productUrl
   * @param {{ image: string, title: string, price: string, priceCents: number } | null} [productSummary] já resolvido pelo painel de confirmação.
   */
  async open(productUrl, productSummary = null) {
    if (!this.refs.content || !productUrl) return;
    this.productSummary = productSummary;
    this.showLoadingSkeleton();
    // Reabrir rápido (fechar+abrir de novo, ou 2 cliques em "escolher
    // lentes") disparava open() concorrente: a chamada antiga, ao resolver
    // depois de um reset()/open() mais novo já ter assumido, sobrescrevia
    // lensPages e empurrava um 2º step de página 1 pro histórico — travava
    // a seleção e deixava entradas fantasma pra navegar de volta. O token
    // marca a chamada mais nova; qualquer outra que resolva depois disso
    // desiste sem tocar em lensPages/histórico.
    const token = ++this.openToken;
    if (!this.lensPagesPromise) {
      this.lensPagesPromise = fetchLensPages(productUrl);
    }
    this.lensPages = await this.lensPagesPromise;
    if (token !== this.openToken) return;
    if (!this.lensPages || !this.lensPages.page1) {
      // Falha real de fetch — sem isso o skeleton acima ficava travado, e
      // saía em silêncio (sem erro visível, ver onExit).
      this.onExit(
        'não foi possível abrir a configuração de lentes agora. tenta de novo.'
      );
      return;
    }

    // Página 1 nunca mostra contador (ver pushStep abaixo) — ela é a única
    // etapa oculta desse fluxo, então o contador (quando aparecer) precisa
    // descontar ela pra começar em "etapa 1".
    this.stepCounterBase = 1;
    splitMonofocalMultifocal(this.lensPages.page1);
    mergeFrameOnlyAndNonRx(this.lensPages.page1);
    if (this.lensPages.page2) {
      injectGradingRecommendationLink(this.lensPages.page2, () =>
        this.handleRecommendClick()
      );
      relabelFreePriceOptions(this.lensPages.page2, {
        note: 'lentes inclusas tem um valor limite no grau, inclua a receita e avalie.*',
      });
    }
    this.pushStep({
      page: this.lensPages.page1,
      title: 'configurar lentes',
      subtitle: 'como você vai usar seus óculos?',
      // Nesse ponto ainda não sabemos se o caminho vai ser grau (4-6
      // etapas), multifocal (próprio fluxo, sem contador) ou sem grau
      // (2-3 etapas) — qualquer número aqui seria chute. Mostra a partir
      // da 2ª etapa, quando o caminho já está decidido.
      hideStepCounter: true,
      onSelect: option => this.advanceFromPage1(option),
      rememberSelection: false,
    });
  }

  /**
   * Grau retroativo em armações solares já no carrinho (a partir do
   * seletor do carrinho) — sempre "com grau", nunca passa pela página 1.
   * @param {Array<any>} frames cada um com {frameVariantId, frameUrl, productSummary}.
   * @param {(results: Array<any>) => Promise<boolean> | void} onComplete
   */
  openBatch(frames, onComplete) {
    if (!frames.length) return;
    // Sem isso, reabrir o seletor reaproveitava history/seleções da
    // tentativa anterior — contador de etapas e voltar quebravam.
    this.reset();
    this.batchQueue = frames;
    this.batchIndex = 0;
    this.batchResults = [];
    this.batchOnComplete = onComplete;
    this.advanceBatchFrame();
  }

  /** Busca a página do frame atual da fila e mostra a etapa de grau (mesmo guard de open()). */
  async advanceBatchFrame() {
    const queue = this.batchQueue;
    const frame = queue?.[this.batchIndex];
    if (!this.refs.content || !queue || !frame) return;
    this.productSummary = frame.productSummary;
    this.showLoadingSkeleton();
    const token = ++this.openToken;
    this.lensPagesPromise = fetchLensPages(frame.frameUrl);
    const lensPages = await this.lensPagesPromise;
    this.lensPages = lensPages;
    // Guardado no próprio item da fila (não em campo solto do controller) —
    // finishCurrentBatchFrame lê de volta pra repassar ao chamador, que
    // precisa disso pra não perder a elegibilidade de brinde da armação
    // ao trocar a linha antiga pela configurada (bug real relatado).
    frame.giftsData = lensPages?.giftsData || null;
    if (token !== this.openToken) return;
    if (!lensPages || !lensPages.page2) {
      // Sem faixa de grau (elegibilidade já devia ter filtrado isso antes) — pula.
      this.finishCurrentBatchFrame();
      return;
    }
    injectGradingRecommendationLink(lensPages.page2, () =>
      this.handleRecommendClick()
    );
    relabelFreePriceOptions(lensPages.page2, {
      note: 'lentes inclusas tem um valor limite no grau, inclua a receita e avalie.*',
    });
    this.pushStep(this.buildPage2Step());
  }

  /** Acumula o frame atual em batchResults e avança pro próximo, ou fecha no resumo do lote. */
  finishCurrentBatchFrame() {
    const queue = this.batchQueue;
    const frame = queue?.[this.batchIndex];
    if (!queue || !frame) return;
    this.batchResults.push({
      frameVariantId: frame.frameVariantId,
      frameVariantTitle: frame.frameVariantTitle,
      cartItemKey: frame.cartItemKey,
      giftsData: frame.giftsData || null,
      kitVariantId: frame.kitVariantId || null,
      kitCaseBanner: frame.kitCaseBanner || null,
      kitCaseDescription: frame.kitCaseDescription || null,
      productSummary: this.productSummary,
      lens: this.finalLensSelection,
      prescription: this.prescriptionValues,
    });
    this.batchIndex += 1;
    this.selectedGradingIndex = null;
    this.selectedGradingTitle = null;
    this.selectedGradingPriceCents = 0;
    this.selectedCoating = null;
    this.finalLensSelection = null;
    this.prescriptionValues = null;
    this.prescriptionValuesGradingTitle = null;
    this.draftPrescriptionValues = null;
    this.highlightedGradingIndex = null;
    // lensPages fica como está — advanceBatchFrame sobrescreve pro próximo
    // frame de qualquer forma, e zerar aqui quebrava getStepTotal() no
    // próprio passo do resumo do lote (que é pushado logo abaixo).
    if (this.batchIndex < queue.length) {
      this.advanceBatchFrame();
    } else {
      this.pushStep(this.buildBatchSummaryStep());
    }
  }

  /**
   * @param {HTMLElement} option
   */
  advanceFromPage1(option) {
    if (option.dataset.multifocal === 'true') {
      // Multifocal exige medição presencial: nunca passa por grau/coating/receita.
      this.pushStep(this.buildLocationPermissionStep());
      return;
    }
    if (option.dataset.semGrauGroup === 'true') {
      const subPage = /** @type {any} */ (option).semGrauSubPage;
      if (!subPage) return;
      // Fixa já no teto do ramo (2: sub-etapa + resumo, já sem contar a
      // página 1 oculta) — nunca cresce depois. Se terminar antes (armação,
      // 1 passo visível real), o total só fica "generoso".
      this.stepTotalOverride = 2;
      this.pushStep(this.buildSemGrauGroupStep(subPage));
      return;
    }
    if (option.dataset.hasChildren === 'true' && this.lensPages?.page2) {
      this.pushStep(this.buildPage2Step());
      return;
    }
    if (option.dataset.variantRef) {
      // Lente própria sem faixa de grau (ex.: filtro de luz azul): pula receita.
      this.selectedGradingTitle =
        option
          .querySelector('.lens-selection__option-button')
          ?.textContent?.trim() || null;
      this.selectedGradingPriceCents = priceCentsFromOption(option);
      this.finalLensSelection = captureLensOptionData(option);
      this.pushStep(this.buildSummaryStep());
      return;
    }
    // Sem grau, sem lente própria: finaliza igual ao solar sem grau.
    if (!this.onAddFrameAndShowCart) return;
    // Sem desabilitar, duplo clique disparava 2 /cart/add.js antes do
    // painel trocar de tela (diferente do resumo, que já se protegia).
    // Essa etapa não troca de página nesse caminho — oneClickLock (setado
    // antes de chegar aqui, ver wireSelectableOption) seguiria travado pra
    // sempre sem esse reset aqui, impedindo retentativa se o add falhar.
    this.setContinueEnabled(false);
    Promise.resolve(this.onAddFrameAndShowCart()).finally(() => {
      this.setContinueEnabled(true);
      this.oneClickLock = false;
    });
  }

  /**
   * @param {HTMLElement} subPage
   * @returns {LensFlowStep}
   */
  buildSemGrauGroupStep(subPage) {
    return {
      page: subPage,
      title: 'óculos sem grau',
      subtitle: 'escolha como prefere levar sua armação',
      onSelect: option => this.advanceFromPage1(option),
      rememberSelection: false,
    };
  }

  /** Volta a pilha até a página 1 (usado pelo aviso multifocal). */
  goToPage1() {
    while (this.history.length > 1) {
      this.goBack();
    }
  }

  /**
   * Pede geolocalização antes de mostrar lojas próximas.
   * @returns {LensFlowStep}
   */
  buildLocationPermissionStep() {
    const page = document.createElement('div');
    page.className = 'lens-selection__page zrzs-quick-add-location';

    const scroll = document.createElement('div');
    scroll.className = 'zrzs-quick-add-location__scroll';
    page.appendChild(scroll);

    const icon = document.createElement('div');
    icon.className = 'zrzs-quick-add-location__icon';
    icon.innerHTML =
      '<svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M12 21s-7-6.5-7-11a7 7 0 1 1 14 0c0 4.5-7 11-7 11z"/><circle cx="12" cy="10" r="2.5"/></svg>';
    scroll.appendChild(icon);

    const title = document.createElement('p');
    title.className = 'zrzs-quick-add-location__title';
    title.textContent = 'permitir localização?';
    scroll.appendChild(title);

    const body = document.createElement('p');
    body.className = 'zrzs-quick-add-location__body';
    body.textContent =
      'Nossas lentes multifocais precisam ser avaliadas presencialmente para garantir uma medição precisa do grau. Para encontrar as lojas mais próximas de você, precisamos acessar sua localização. Assim, podemos indicar onde experimentar seus modelos favoritos.';
    scroll.appendChild(body);

    const ctaGroup = document.createElement('div');
    ctaGroup.className = 'zrzs-quick-add-location__cta';

    const allowButton = document.createElement('button');
    allowButton.type = 'button';
    allowButton.className = 'zrzs-quick-add-location__allow-button';
    allowButton.textContent = 'permitir acesso';
    allowButton.addEventListener('click', () => {
      if (!navigator.geolocation) {
        this.pushStep(this.buildMultifocalNoticeStep());
        return;
      }
      allowButton.disabled = true;
      navigator.geolocation.getCurrentPosition(
        position => {
          this.pushStep(
            this.buildNearbyStoresStep(
              position.coords.latitude,
              position.coords.longitude
            )
          );
        },
        error => {
          // PERMISSION_DENIED=1, POSITION_UNAVAILABLE=2, TIMEOUT=3.
          // eslint-disable-next-line no-console
          console.warn(
            '[QuickAddLensFlow] Geolocalização falhou:',
            error?.code,
            error?.message
          );
          this.pushStep(this.buildMultifocalNoticeStep());
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    });
    ctaGroup.appendChild(allowButton);

    const denyLink = document.createElement('button');
    denyLink.type = 'button';
    denyLink.className = 'zrzs-quick-add-location__deny-link';
    denyLink.textContent = 'agora não';
    denyLink.addEventListener('click', () =>
      this.pushStep(this.buildMultifocalNoticeStep())
    );
    ctaGroup.appendChild(denyLink);

    page.appendChild(ctaGroup);

    return {
      page,
      title: 'configurar lentes',
      subtitle: 'multifocal disponível apenas em loja',
      custom: true,
      hideFooter: true,
      hideStepCounter: true,
      hideHeaderChrome: true,
      // Marca toda a sub-pilha a partir daqui como multifocal — fechar o
      // overlay (⊗/backdrop) nessa etapa ou em qualquer uma depois dela
      // (sem passar pelo ← de volta até a página 1) precisa resetar o
      // resume (ver isInMultifocalFlow/zrzs-quick-add-overlay.js#close).
      isMultifocal: true,
      // "permitir acesso" fica disabled durante getCurrentPosition — sem
      // isso, voltar a essa etapa reusaria o botão ainda desabilitado.
      onRender: () => {
        allowButton.disabled = false;
      },
    };
  }

  /** Alguma etapa da pilha atual é a do fluxo multifocal (sem volta pra página 1). */
  isInMultifocalFlow() {
    return this.history.some(step => step.isMultifocal === true);
  }

  /**
   * Card de loja reaproveitado em lojas próximas, diretória e busca por
   * CEP. address/opening_hours usam innerHTML — já vêm com <br/> do
   * dado do merchant.
   * @param {import('./zrzs-quick-add-stores.js').Store & { distanceKm?: number }} store
   * @param {{ showDistance?: boolean }} [options]
   * @returns {HTMLElement}
   */
  buildStoreCardNode(store, options = {}) {
    const card = document.createElement('div');
    card.className = 'zrzs-quick-add-store-card';

    const top = document.createElement('div');
    top.className = 'zrzs-quick-add-store-card__top';
    const name = document.createElement('p');
    name.className = 'zrzs-quick-add-store-card__name';
    name.textContent = store.title || '';
    top.appendChild(name);
    if (options.showDistance && typeof store.distanceKm === 'number') {
      const badge = document.createElement('span');
      badge.className = 'zrzs-quick-add-store-card__distance';
      badge.textContent = formatDistanceKm(store.distanceKm);
      top.appendChild(badge);
    }
    card.appendChild(top);

    if (store.opening_hours) {
      const hours = document.createElement('p');
      hours.className = 'zrzs-quick-add-store-card__hours';
      hours.innerHTML = store.opening_hours;
      card.appendChild(hours);
    }

    if (store.address) {
      const address = document.createElement('p');
      address.className = 'zrzs-quick-add-store-card__address';
      address.innerHTML = store.address;
      card.appendChild(address);
    }

    const actions = document.createElement('div');
    actions.className = 'zrzs-quick-add-store-card__actions';

    const directionsLink = document.createElement('a');
    directionsLink.className =
      'zrzs-quick-add-store-card__action zrzs-quick-add-store-card__action--primary';
    directionsLink.textContent = 'como chegar';
    directionsLink.target = '_blank';
    directionsLink.rel = 'noreferrer noopener';
    directionsLink.href = buildDirectionsLink(store);
    actions.appendChild(directionsLink);

    const contactLink = document.createElement('a');
    contactLink.className = 'zrzs-quick-add-store-card__action';
    contactLink.textContent = 'contato';
    contactLink.target = '_blank';
    contactLink.rel = 'noreferrer noopener';
    contactLink.href = buildWhatsappLink(store.wpp_contact);
    actions.appendChild(contactLink);

    card.appendChild(actions);
    return card;
  }

  /**
   * Card usado só na diretória completa. wpp_contact já vem formatado
   * do merchant, sem máscara aqui.
   * @param {import('./zrzs-quick-add-stores.js').Store} store
   * @returns {HTMLElement}
   */
  buildDirectoryStoreCardNode(store) {
    const card = document.createElement('div');
    card.className = 'zrzs-quick-add-directory-card';

    const header = document.createElement('div');
    header.className = 'zrzs-quick-add-directory-card__header';
    const icon = document.createElement('span');
    icon.className = 'zrzs-quick-add-directory-card__icon';
    icon.innerHTML =
      '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 21s-7-6.5-7-11a7 7 0 1 1 14 0c0 4.5-7 11-7 11z"/><circle cx="12" cy="10" r="2.5"/></svg>';
    header.appendChild(icon);
    const name = document.createElement('p');
    name.className = 'zrzs-quick-add-directory-card__name';
    name.textContent = store.title || '';
    header.appendChild(name);
    card.appendChild(header);

    if (store.address) {
      const address = document.createElement('p');
      address.className = 'zrzs-quick-add-directory-card__address';
      address.innerHTML = store.address;
      card.appendChild(address);
    }

    if (store.wpp_contact || store.opening_hours) {
      const meta = document.createElement('div');
      meta.className = 'zrzs-quick-add-directory-card__meta';
      if (store.wpp_contact) {
        const phone = document.createElement('p');
        phone.className = 'zrzs-quick-add-directory-card__phone';
        phone.textContent = `contato: ${store.wpp_contact}`;
        meta.appendChild(phone);
      }
      if (store.opening_hours) {
        const hours = document.createElement('p');
        hours.className = 'zrzs-quick-add-directory-card__hours';
        hours.innerHTML = store.opening_hours;
        meta.appendChild(hours);
      }
      card.appendChild(meta);
    }

    const actions = document.createElement('div');
    actions.className = 'zrzs-quick-add-directory-card__actions';
    const directionsLink = document.createElement('a');
    directionsLink.className = 'zrzs-quick-add-directory-card__action';
    directionsLink.textContent = 'como chegar →';
    directionsLink.target = '_blank';
    directionsLink.rel = 'noreferrer noopener';
    directionsLink.href = buildDirectionsLink(store);
    actions.appendChild(directionsLink);
    card.appendChild(actions);

    return card;
  }

  /**
   * Reaproveita o conteúdo cadastrado em
   * sections/stores-locations-page-new.liquid (bloco "noStoresFound").
   * @param {import('./zrzs-quick-add-stores.js').NoStoresFound | null} [noStoresFound]
   * @returns {HTMLElement}
   */
  buildNoStoresFoundNode(noStoresFound) {
    const wrap = document.createElement('div');
    wrap.className = 'zrzs-quick-add-no-stores';

    if (!noStoresFound) {
      const fallback = document.createElement('p');
      fallback.className = 'zrzs-quick-add-no-stores__description';
      fallback.textContent =
        'poxa, no momento ainda não chegamos a sua cidade ::(';
      wrap.appendChild(fallback);
      return wrap;
    }

    if (noStoresFound.headline) {
      const headline = document.createElement('p');
      headline.className = 'zrzs-quick-add-no-stores__headline';
      headline.textContent = noStoresFound.headline.toLowerCase();
      wrap.appendChild(headline);
    }
    if (noStoresFound.title) {
      const title = document.createElement('p');
      title.className = 'zrzs-quick-add-no-stores__title';
      title.textContent = noStoresFound.title;
      wrap.appendChild(title);
    }
    if (noStoresFound.description) {
      const description = document.createElement('p');
      description.className = 'zrzs-quick-add-no-stores__description';
      description.textContent = noStoresFound.description;
      wrap.appendChild(description);
    }

    // Links vazios (não preenchidos nos settings ainda) não geram <a href="#">.
    const buttons = [noStoresFound.button1, noStoresFound.button2].filter(
      b => b && b.text && b.link
    );
    if (buttons.length) {
      const actions = document.createElement('div');
      actions.className = 'zrzs-quick-add-no-stores__actions';
      buttons.forEach(b => {
        const link = document.createElement('a');
        link.className = 'zrzs-quick-add-no-stores__action';
        link.textContent = b.text;
        link.href = b.link;
        actions.appendChild(link);
      });
      wrap.appendChild(actions);
    }

    return wrap;
  }

  /**
   * Lojas dentro de 50km, ordenadas por distância.
   * @param {number} userLat
   * @param {number} userLon
   * @returns {LensFlowStep}
   */
  buildNearbyStoresStep(userLat, userLon) {
    const page = document.createElement('div');
    page.className = 'lens-selection__page zrzs-quick-add-stores-page';

    const mapPlaceholder = document.createElement('div');
    mapPlaceholder.className = 'zrzs-quick-add-stores-page__map-placeholder';
    if (this.storesMapImageUrl) {
      mapPlaceholder.style.backgroundImage = `url("${this.storesMapImageUrl}")`;
    }
    page.appendChild(mapPlaceholder);

    const listWrap = document.createElement('div');
    listWrap.className = 'zrzs-quick-add-stores-page__list';
    page.appendChild(listWrap);

    const step = {
      page,
      title: 'lojas próximas',
      subtitle: '',
      custom: true,
      hideFooter: true,
      hideStepCounter: true,
      onRender: async () => {
        listWrap.innerHTML =
          '<p class="zrzs-quick-add-stores-page__status">buscando lojas...</p>';
        const data = await getCachedStoresData();
        // Evita renderizar numa etapa que o usuário já deixou.
        if (this.history[this.history.length - 1] !== step) return;
        if (!data) {
          // Falha real de rede — não confundir com "nenhuma loja aqui"
          // (buildNoStoresFoundNode reaproveita o conteúdo do tema pra
          // esse segundo caso, mais abaixo).
          listWrap.innerHTML =
            '<p class="zrzs-quick-add-stores-page__status">não conseguimos carregar as lojas agora. tenta de novo em alguns instantes.</p>';
          return;
        }
        const nearby = sortStoresByDistance(data.stores, userLat, userLon).filter(
          store => store.distanceKm <= 50
        );
        this.setStepCounterText(
          `${nearby.length} encontrada${nearby.length === 1 ? '' : 's'}`
        );
        listWrap.innerHTML = '';
        if (!nearby.length) {
          listWrap.appendChild(this.buildNoStoresFoundNode(data.noStoresFound));
          return;
        }
        nearby.forEach(store => {
          listWrap.appendChild(
            this.buildStoreCardNode(store, { showDistance: true })
          );
        });
      },
    };
    return step;
  }

  /**
   * Diretória completa, busca por texto + chip de cidade.
   * @returns {LensFlowStep}
   */
  buildStoreDirectoryStep() {
    const page = document.createElement('div');
    page.className = 'lens-selection__page zrzs-quick-add-directory';

    const searchSection = document.createElement('div');
    searchSection.className = 'zrzs-quick-add-directory__search-section';

    const searchField = document.createElement('div');
    searchField.className = 'zrzs-quick-add-directory__search-field';
    const searchIcon = document.createElement('span');
    searchIcon.className = 'zrzs-quick-add-directory__search-icon';
    searchIcon.innerHTML =
      '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true"><circle cx="6" cy="6" r="5" stroke="currentColor" stroke-width="1.3"/><path d="M13 13L9.5 9.5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>';
    searchField.appendChild(searchIcon);

    const searchInput = document.createElement('input');
    searchInput.type = 'text';
    // Filtra city/state/title; não resolve CEP (isso é só no aviso multifocal).
    searchInput.placeholder = 'buscar...';
    searchInput.className = 'zrzs-quick-add-directory__search';
    searchField.appendChild(searchInput);
    searchSection.appendChild(searchField);

    const chipsRow = document.createElement('div');
    chipsRow.className = 'zrzs-quick-add-directory__chips';
    searchSection.appendChild(chipsRow);
    page.appendChild(searchSection);

    const listWrap = document.createElement('div');
    listWrap.className = 'zrzs-quick-add-directory__list';
    page.appendChild(listWrap);

    let allStores = /** @type {any[]} */ ([]);
    let activeCity = /** @type {string | null} */ (null);

    const renderList = () => {
      // Evita renderizar numa etapa que o usuário já deixou.
      if (this.history[this.history.length - 1] !== step) return;
      const term = searchInput.value.trim().toLowerCase();
      const filtered = allStores.filter(store => {
        if (activeCity && store.city !== activeCity) return false;
        if (!term) return true;
        return (
          (store.city || '').toLowerCase().includes(term) ||
          (store.state || '').toLowerCase().includes(term) ||
          (store.title || '').toLowerCase().includes(term)
        );
      });
      this.setStepCounterText(
        `${filtered.length} loja${filtered.length === 1 ? '' : 's'}`
      );
      listWrap.innerHTML = '';
      if (!filtered.length) {
        const empty = document.createElement('p');
        empty.className = 'zrzs-quick-add-directory__empty';
        empty.textContent = 'nenhuma loja encontrada.';
        listWrap.appendChild(empty);
        return;
      }
      filtered.forEach(store => {
        listWrap.appendChild(this.buildDirectoryStoreCardNode(store));
      });
    };
    searchInput.addEventListener('input', renderList);

    const step = {
      page,
      title: 'nossas lojas',
      subtitle: '',
      custom: true,
      hideFooter: true,
      hideStepCounter: true,
      onRender: async () => {
        listWrap.innerHTML =
          '<p class="zrzs-quick-add-directory__empty">carregando...</p>';
        const data = await getCachedStoresData();
        if (this.history[this.history.length - 1] !== step) return;
        allStores = data?.stores || [];

        const cities = [
          ...new Set(allStores.map(store => store.city).filter(Boolean)),
        ].sort((a, b) => a.localeCompare(b));

        chipsRow.innerHTML = '';
        /** @param {HTMLElement} activeChip */
        const setActiveChip = activeChip => {
          chipsRow
            .querySelectorAll('.zrzs-quick-add-directory__chip')
            .forEach(chip =>
              chip.classList.remove('zrzs-quick-add-directory__chip--active')
            );
          activeChip.classList.add('zrzs-quick-add-directory__chip--active');
        };

        const allChip = document.createElement('button');
        allChip.type = 'button';
        allChip.className =
          'zrzs-quick-add-directory__chip zrzs-quick-add-directory__chip--active';
        allChip.textContent = 'todas';
        allChip.addEventListener('click', () => {
          activeCity = null;
          setActiveChip(allChip);
          renderList();
        });
        chipsRow.appendChild(allChip);

        cities.forEach(city => {
          const chip = document.createElement('button');
          chip.type = 'button';
          chip.className = 'zrzs-quick-add-directory__chip';
          chip.textContent = city.toLowerCase();
          chip.addEventListener('click', () => {
            activeCity = city;
            setActiveChip(chip);
            renderList();
          });
          chipsRow.appendChild(chip);
        });

        renderList();
      },
    };
    return step;
  }

  /**
   * Aviso de multifocal. Busca por CEP resolve a cidade via ViaCEP, sem
   * distância (ViaCEP não devolve lat/long).
   * @returns {LensFlowStep}
   */
  buildMultifocalNoticeStep() {
    const page = document.createElement('div');
    page.className = 'lens-selection__page zrzs-quick-add-multifocal';

    const alert = document.createElement('div');
    alert.className = 'zrzs-quick-add-multifocal__alert';
    const alertHeader = document.createElement('div');
    alertHeader.className = 'zrzs-quick-add-multifocal__alert-header';
    const alertIcon = document.createElement('div');
    alertIcon.className = 'zrzs-quick-add-multifocal__alert-icon';
    alertIcon.innerHTML =
      '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M12 21s-7-6.5-7-11a7 7 0 1 1 14 0c0 4.5-7 11-7 11z"/><circle cx="12" cy="10" r="2.5"/></svg>';
    alertHeader.appendChild(alertIcon);
    const alertTitle = document.createElement('p');
    alertTitle.className = 'zrzs-quick-add-multifocal__alert-title';
    alertTitle.textContent = 'multifocal disponível apenas em loja';
    alertHeader.appendChild(alertTitle);
    alert.appendChild(alertHeader);
    const alertBody = document.createElement('p');
    alertBody.className = 'zrzs-quick-add-multifocal__alert-body';
    alertBody.textContent =
      'as lentes multifocais precisam de medições presenciais que só podem ser feitas em uma das nossas lojas. não encontramos uma loja perto de você.';
    alert.appendChild(alertBody);
    page.appendChild(alert);

    const suggestions = document.createElement('div');
    suggestions.className = 'zrzs-quick-add-multifocal__suggestions';

    const suggestionsLabel = document.createElement('p');
    suggestionsLabel.className = 'zrzs-quick-add-multifocal__suggestions-label';
    suggestionsLabel.textContent = 'o que você pode fazer:';
    suggestions.appendChild(suggestionsLabel);

    const arrowRightSvg =
      '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M8 3.3328L12.6672 8L8 12.6672M12.6672 8H3.3328" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';

    /**
     * Card reaproveitado nos dois blocos abaixo. Não usa
     * .lens-selection__option--recommended (deixa o título verde).
     * @param {{ title: string, description: string, recommended?: boolean, onClick: () => void }} config
     */
    const buildOptionCard = ({ title, description, recommended, onClick }) => {
      const option = document.createElement('div');
      option.className = recommended
        ? 'lens-selection__option zrzs-quick-add-multifocal__option zrzs-quick-add-multifocal__option--recommended'
        : 'lens-selection__option zrzs-quick-add-multifocal__option';

      const topRow = document.createElement('div');
      topRow.className = 'zrzs-quick-add-multifocal__option-top';

      const titleGroup = document.createElement('div');
      titleGroup.className = 'zrzs-quick-add-multifocal__option-title-group';
      const titleEl = document.createElement('p');
      titleEl.className = 'lens-selection__option-button';
      titleEl.textContent = title;
      titleGroup.appendChild(titleEl);
      if (recommended) {
        const tag = document.createElement('span');
        tag.className = 'zrzs-quick-add-multifocal__option-tag';
        tag.textContent = 'recomendado';
        titleGroup.appendChild(tag);
      }
      topRow.appendChild(titleGroup);

      const arrow = document.createElement('span');
      arrow.className = 'zrzs-quick-add-multifocal__option-arrow';
      arrow.innerHTML = arrowRightSvg;
      topRow.appendChild(arrow);

      option.appendChild(topRow);

      const descriptionEl = document.createElement('p');
      descriptionEl.className = 'lens-selection__option-description';
      descriptionEl.textContent = description;
      option.appendChild(descriptionEl);

      option.addEventListener('click', onClick);
      return option;
    };

    suggestions.appendChild(
      buildOptionCard({
        title: 'levar só a armação',
        description:
          'compre a armação e faça as lentes multifocais em uma ótica na sua cidade',
        recommended: true,
        onClick: () => {
          if (this.onAddFrameAndShowCart) this.onAddFrameAndShowCart();
        },
      })
    );

    suggestions.appendChild(
      buildOptionCard({
        title: 'ver nossas lojas',
        description:
          'confira os endereços das nossas lojas para uma visita presencial',
        onClick: () => this.pushStep(this.buildStoreDirectoryStep()),
      })
    );

    const cepSection = document.createElement('div');
    cepSection.className = 'zrzs-quick-add-multifocal__cep';
    const cepLabel = document.createElement('p');
    cepLabel.className = 'zrzs-quick-add-multifocal__cep-label';
    cepLabel.textContent = 'ou busque por cep';
    cepSection.appendChild(cepLabel);

    const cepRow = document.createElement('div');
    cepRow.className = 'zrzs-quick-add-multifocal__cep-row';
    const cepInput = document.createElement('input');
    cepInput.type = 'text';
    cepInput.inputMode = 'numeric';
    cepInput.placeholder = '00000-000';
    cepInput.maxLength = 9;
    cepInput.className = 'zrzs-quick-add-multifocal__cep-input';
    cepInput.addEventListener('input', () => {
      cepInput.value = formatCepInput(cepInput.value);
    });
    cepRow.appendChild(cepInput);

    const cepButton = document.createElement('button');
    cepButton.type = 'button';
    cepButton.className = 'zrzs-quick-add-multifocal__cep-button';
    cepButton.textContent = 'buscar';
    cepRow.appendChild(cepButton);
    cepSection.appendChild(cepRow);

    const cepHint = document.createElement('p');
    cepHint.className = 'zrzs-quick-add-multifocal__cep-hint';
    cepHint.textContent = 'verifique se há uma loja perto de você';
    cepSection.appendChild(cepHint);
    suggestions.appendChild(cepSection);
    page.appendChild(suggestions);

    const cepResults = document.createElement('div');
    cepResults.className = 'zrzs-quick-add-multifocal__cep-results';
    cepResults.hidden = true;
    page.appendChild(cepResults);

    cepButton.addEventListener('click', async () => {
      const digits = cepInput.value.replace(/\D/g, '');
      if (digits.length !== 8) return;
      cepButton.disabled = true;
      cepResults.hidden = false;
      cepResults.innerHTML =
        '<p class="zrzs-quick-add-multifocal__cep-status">buscando...</p>';
      try {
        const city = await lookupCepCity(digits);
        if (!city) {
          cepResults.innerHTML =
            '<p class="zrzs-quick-add-multifocal__cep-status">cep não encontrado.</p>';
          return;
        }
        const data = await getCachedStoresData();
        if (!data) {
          // Falha real de rede — distinto de "sem loja nessa cidade" abaixo.
          cepResults.innerHTML =
            '<p class="zrzs-quick-add-multifocal__cep-status">não conseguimos carregar as lojas agora, tenta de novo.</p>';
          return;
        }
        const matches = (data.stores || []).filter(
          store => normalizeCityName(store.city) === normalizeCityName(city)
        );

        cepResults.innerHTML = '';
        const resultsLabel = document.createElement('p');
        resultsLabel.className = 'zrzs-quick-add-multifocal__cep-results-label';
        resultsLabel.textContent = 'lojas na sua cidade';
        cepResults.appendChild(resultsLabel);

        if (!matches.length) {
          const empty = document.createElement('p');
          empty.className = 'zrzs-quick-add-multifocal__cep-status';
          empty.textContent =
            'poxa, no momento ainda não chegamos a sua cidade ::(';
          cepResults.appendChild(empty);
          return;
        }
        matches.forEach(store => {
          cepResults.appendChild(
            this.buildStoreCardNode(store, { showDistance: false })
          );
        });
      } catch (error) {
        cepResults.innerHTML =
          '<p class="zrzs-quick-add-multifocal__cep-status">não conseguimos buscar agora, tenta de novo.</p>';
      } finally {
        cepButton.disabled = false;
      }
    });

    const backLink = document.createElement('button');
    backLink.type = 'button';
    backLink.className = 'zrzs-quick-add-multifocal__back-link';
    backLink.textContent = 'voltar para opções de lente →';
    backLink.addEventListener('click', () => this.goToPage1());
    page.appendChild(backLink);

    return {
      page,
      title: 'configurar lentes',
      subtitle: 'aviso sobre lentes multifocais',
      custom: true,
      hideFooter: true,
      hideStepCounter: true,
    };
  }

  /**
   * @returns {LensFlowStep}
   */
  buildPage2Step() {
    const page2 = /** @type {HTMLElement} */ (this.lensPages?.page2);
    injectConsultantCard(page2, this.consultantWhatsappNumber);
    /** @param {HTMLElement} gradingOption */
    const onSelect = gradingOption => this.advanceFromPage2(gradingOption);
    return {
      page: page2,
      title: 'configurar lentes',
      subtitle: 'de acordo com seu grau, escolha suas lentes ::)',
      onSelect,
      rememberSelection: false,
      onRender: () => {
        const recommendLink = page2.querySelector(
          '.zrzs-quick-add-lens-flow__recommend-link'
        );
        if (recommendLink) {
          /** @type {HTMLElement} */ (recommendLink).hidden = Boolean(
            this.prescriptionValues
          );
        }

        page2
          .querySelectorAll('.lens-selection__option--recommended')
          .forEach(option =>
            option.classList.remove('lens-selection__option--recommended')
          );

        // Veio de "indique minha lente pela receita": omite as faixas
        // inferiores à recomendada (não dá pra comprar uma lente que não
        // atende a própria receita digitada) — a mais cara continua à
        // venda, só o destaque verde muda. display inline (não `hidden`)
        // porque .lens-selection__option já tem `display: flex` de CSS de
        // autor, que ganha do `[hidden]` do user-agent (mesma classe de bug
        // de especificidade já documentada noutros pontos do fluxo).
        const minGradingIndex =
          this.highlightedGradingIndex != null
            ? Number(this.highlightedGradingIndex)
            : null;
        page2.querySelectorAll('.lens-selection__option').forEach(el => {
          const option = /** @type {HTMLElement} */ (el);
          const optionIndex = Number(option.dataset.gradingIndex);
          const shouldHide =
            minGradingIndex != null &&
            !Number.isNaN(optionIndex) &&
            optionIndex < minGradingIndex;
          option.style.display = shouldHide ? 'none' : '';
        });

        const target = this.highlightedGradingIndex
          ? /** @type {HTMLElement | null} */ (
              page2.querySelector(
                `.lens-selection__option[data-grading-index="${this.highlightedGradingIndex}"]`
              )
            )
          : null;
        if (target) {
          target.classList.add('lens-selection__option--recommended');
          target.classList.add('is-selected');
          this.pendingSelection = { option: target, onSelect };
          this.setContinueEnabled(true);
        } else {
          this.pendingSelection = null;
          this.setContinueEnabled(false);
        }
      },
    };
  }

  /**
   * @param {HTMLElement} option
   */
  advanceFromPage2(option) {
    const gradingIndex = option.dataset.gradingIndex;
    const newTitle =
      option
        .querySelector('.lens-selection__option-button')
        ?.textContent?.trim() || null;

    if (this.prescriptionValues) {
      if (
        this.prescriptionValuesGradingTitle !== null &&
        newTitle !== this.prescriptionValuesGradingTitle
      ) {
        // Faixa realmente mudou depois de já ter receita confirmada —
        // invalida pra reconferir contra o novo limite (ver advanceFromPage3).
        this.prescriptionValues = null;
        this.prescriptionValuesGradingTitle = null;
      } else {
        // Mesma faixa de antes (reabriu só pra trocar a proteção extra, por
        // exemplo), ou ainda não tinha faixa travada (vindo da
        // recomendação) — mantém a receita e trava nessa faixa agora.
        this.prescriptionValuesGradingTitle = newTitle;
      }
    }

    if (gradingIndex) this.selectedGradingIndex = gradingIndex;
    this.selectedGradingTitle = newTitle;
    this.selectedGradingPriceCents = priceCentsFromOption(option);

    // Sem coating_options: a própria opção de grading é a lente final.
    if (
      option.dataset.hasChildren !== 'true' ||
      !gradingIndex ||
      !this.lensPages?.page3
    ) {
      if (option.dataset.variantRef) {
        this.finalLensSelection = captureLensOptionData(option);
      }
      this.advanceFromPage3();
      return;
    }

    filterCoatingOptionsByGrading(this.lensPages.page3, gradingIndex);
    relabelFreePriceOptions(this.lensPages.page3);
    this.pushStep({
      page: this.lensPages.page3,
      title: 'configurar lentes',
      subtitle: 'deseja proteção extra?',
      footnote:
        'o filtro de luz azul pode deixar um residual azulado ou esverdeado nas lentes.*',
      onSelect: coatingOption => this.advanceFromPage3(coatingOption),
      rememberSelection: false,
    });
  }

  /**
   * Se já há receita salva, pula direto pro resumo. Senão, pergunta como
   * o cliente prefere seguir (buildPrescriptionChoiceStep) em vez de já
   * cair direto na etapa de receita (formulário manual OD/OE, sem OCR).
   * @param {HTMLElement} [option] coating escolhido, ausente se a faixa não tem.
   */
  advanceFromPage3(option) {
    if (option) {
      // selectedCoating vira delta só para exibição (ver toCoatingDelta).
      const coatingData = captureLensOptionData(option);
      this.finalLensSelection = coatingData;
      this.selectedCoating = toCoatingDelta(
        coatingData,
        this.selectedGradingPriceCents
      );
    }
    if (this.prescriptionValues) {
      if (this.batchQueue) {
        this.finishCurrentBatchFrame();
        return;
      }
      this.pushStep(this.buildSummaryStep());
      return;
    }
    this.pushStep(this.buildPrescriptionChoiceStep());
  }

  /**
   * Entre proteção extra e receita: pergunta se prefere preencher a
   * receita agora ou só depois da compra, em vez de já cair direto na
   * etapa de receita. Mesmo padrão visual de opção das outras etapas
   * (reaproveita .lens-selection__option — ver mergeFrameOnlyAndNonRx).
   * @returns {LensFlowStep}
   */
  buildPrescriptionChoiceStep() {
    const page = document.createElement('div');
    page.className = 'lens-selection__page zrzs-quick-add-prescription-choice';

    const optionsWrap = document.createElement('div');
    optionsWrap.className = 'lens-selection__options';
    page.appendChild(optionsWrap);

    /**
     * @param {string} title
     * @param {string} description
     * @param {string} choice
     */
    const buildChoiceOption = (title, description, choice) => {
      const option = document.createElement('div');
      option.className = 'lens-selection__option';
      option.dataset.prescriptionChoice = choice;
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'lens-selection__option-button';
      button.textContent = title;
      option.appendChild(button);
      const descriptionEl = document.createElement('p');
      descriptionEl.className = 'lens-selection__option-description';
      descriptionEl.textContent = description;
      option.appendChild(descriptionEl);
      return option;
    };

    optionsWrap.appendChild(
      buildChoiceOption(
        'preencher receita agora',
        'insira os dados da sua receita agora e já deixe tudo certo pra produção das lentes.',
        'now'
      )
    );
    const divider = document.createElement('div');
    divider.className = 'lens-selection__option-divider';
    optionsWrap.appendChild(divider);
    optionsWrap.appendChild(
      buildChoiceOption(
        'enviar receita após a compra',
        'finalize a compra tranquilo e envie a receita depois — só começamos a produção quando ela chegar.',
        'later'
      )
    );

    return {
      page,
      title: 'configurar lentes',
      subtitle: 'como deseja seguir com sua receita?',
      onSelect: option => this.advanceFromPrescriptionChoice(option),
      rememberSelection: false,
    };
  }

  /** @param {HTMLElement} option */
  advanceFromPrescriptionChoice(option) {
    if (option.dataset.prescriptionChoice === 'later') {
      // Mesmo efeito de "enviar depois" dentro da própria etapa de receita
      // (ver buildPrescriptionStep#onSkip) — só que sem nunca chegar lá.
      this.willFillPrescriptionNow = false;
      this.prescriptionValues = null;
      this.prescriptionValuesGradingTitle = null;
      if (this.batchQueue) {
        this.finishCurrentBatchFrame();
        return;
      }
      this.pushStep(this.buildSummaryStep());
      return;
    }
    this.willFillPrescriptionNow = true;
    this.pushStep(this.buildPrescriptionStep());
  }

  /** "indique minha lente pela receita" — mesma etapa, marcada (viaRecommend) para voltar à página 2 destacada. */
  handleRecommendClick() {
    this.recommendUsedInThisPass = true;
    this.pushStep(this.buildPrescriptionStep(true));
  }

  /**
   * Marca `highlightedGradingIndex`; sincronização no DOM ocorre em
   * buildPage2Step.
   * @returns {boolean} false se nenhuma faixa cobre os valores digitados.
   */
  applyGradingHighlightFromValues() {
    if (!this.lensPages?.page2 || !this.prescriptionValues) return false;
    const required = findRequiredGradingOption(
      this.lensPages.page2,
      this.prescriptionValues
    );
    if (!required?.gradingIndex) return false;
    this.highlightedGradingIndex = required.gradingIndex;
    return true;
  }

  /**
   * applyRequiredGradingUpgrade muta selectedGradingTitle/Index direto no
   * estado, sem pushStep próprio — um goBack() que volte até ANTES desse
   * upgrade restaura o snapshot pré-upgrade (via captureSelectionState),
   * desfazendo-o. Chamado por goBack() a cada navegação (não só quando a
   * etapa de receita é revisitada): advanceFromPage3() pula DIRETO pro
   * resumo quando prescriptionValues já existe, sem nunca re-renderizar
   * aquela etapa — um resync só no onRender dela não bastava (bug real:
   * voltar 2x até page3, reselecionar proteção, resumo saía com a faixa
   * e o preço errados, de antes do upgrade). Também refiltra page3 pela
   * faixa certa, senão o cliente veria/clicaria nas opções da faixa
   * velha (DOM nunca é re-filtrado sozinho, só no avanço normal por
   * page2 → advanceFromPage2).
   * @returns {boolean} true se havia algo pra resincronizar
   */
  resyncGradingFromConfirmedPrescription() {
    if (
      !this.prescriptionValues ||
      !this.prescriptionValuesGradingTitle ||
      this.prescriptionValuesGradingTitle === this.selectedGradingTitle ||
      !this.lensPages?.page2
    ) {
      return false;
    }
    const required = findRequiredGradingOption(
      this.lensPages.page2,
      this.prescriptionValues
    );
    if (!required?.gradingIndex) return false;
    this.applyRequiredGradingUpgrade(required.gradingIndex);
    if (this.lensPages.page3) {
      filterCoatingOptionsByGrading(
        this.lensPages.page3,
        required.gradingIndex
      );
    }
    return true;
  }

  /**
   * Aplica o upgrade de faixa de grau (e coating) direto no estado, sem
   * passar pelo clique manual em page2/page3.
   * @param {string} requiredGradingIndex
   */
  applyRequiredGradingUpgrade(requiredGradingIndex) {
    const option = /** @type {HTMLElement | null | undefined} */ (
      this.lensPages?.page2?.querySelector(
        `.lens-selection__option[data-grading-index="${requiredGradingIndex}"]`
      )
    );
    if (!option) return;

    const previousCoatingTitle = this.selectedCoating?.title || null;
    this.selectedGradingIndex = requiredGradingIndex;
    this.selectedGradingTitle =
      option
        .querySelector('.lens-selection__option-button')
        ?.textContent?.trim() || null;
    this.selectedGradingPriceCents = priceCentsFromOption(option);

    if (option.dataset.hasChildren !== 'true' || !this.lensPages?.page3) {
      this.selectedCoating = null;
      if (option.dataset.variantRef) {
        this.finalLensSelection = captureLensOptionData(option);
      }
      return;
    }

    const coatingOption = findCoatingOptionForGrading(
      this.lensPages.page3,
      requiredGradingIndex,
      previousCoatingTitle
    );
    if (!coatingOption) return;
    const coatingData = captureLensOptionData(coatingOption);
    this.finalLensSelection = coatingData;
    this.selectedCoating = toCoatingDelta(
      coatingData,
      this.selectedGradingPriceCents
    );
  }

  /**
   * Etapa custom: formulário OD/OE + aviso de grau + "enviar depois".
   * @param {boolean} [viaRecommend] veio de "indique minha lente pela receita".
   * @returns {LensFlowStep}
   */
  buildPrescriptionStep(viaRecommend = false) {
    const page = document.createElement('div');
    page.className = 'lens-selection__page zrzs-quick-add-prescription';

    // Fluxo em lote com MAIS de 1 armação — mostra em qual delas estamos,
    // igual ao Figma "óculos X de Y" (com só 1, o Figma não mostra nada).
    if (this.batchQueue && this.batchQueue.length > 1) {
      const indicator = document.createElement('p');
      indicator.className = 'zrzs-quick-add-prescription__batch-indicator';
      indicator.textContent = `óculos ${this.batchIndex + 1} de ${this.batchQueue.length}`;
      page.appendChild(indicator);
    }

    if (this.productSummary) {
      const card = document.createElement('div');
      card.className = 'zrzs-quick-add-prescription__product';
      card.innerHTML = `
        <img class="zrzs-quick-add-prescription__product-image" src="${this.productSummary.image}" alt="" width="48" height="48" loading="lazy">
        <div class="zrzs-quick-add-prescription__product-info">
          <p class="zrzs-quick-add-prescription__product-name"></p>
        </div>
        <p class="zrzs-quick-add-prescription__product-price"></p>
      `;
      card.querySelector(
        '.zrzs-quick-add-prescription__product-name'
      ).textContent = this.productSummary.title;
      card.querySelector(
        '.zrzs-quick-add-prescription__product-price'
      ).textContent = this.productSummary.price;
      page.appendChild(card);
    }

    // Sempre no DOM (só alterna hidden) pra não empurrar o layout ao aparecer.
    const warning = document.createElement('div');
    warning.className = 'zrzs-quick-add-prescription__warning';
    warning.hidden = true;
    warning.innerHTML = `
      <svg class="zrzs-quick-add-prescription__warning-icon" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <circle cx="8" cy="8" r="7" stroke="currentColor" stroke-width="1.3"/>
        <path d="M8 7.3V11.5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/>
        <circle cx="8" cy="4.8" r="0.9" fill="currentColor"/>
      </svg>
      <p class="zrzs-quick-add-prescription__warning-text">
        <span data-warning-message></span>
        <button type="button" class="zrzs-quick-add-prescription__warning-link" data-warning-link>saiba mais</button>
      </p>
    `;

    const odFields = this.buildPrescriptionSection('olho direito (od)*', [
      { key: 'odSphere', label: 'esférico', kind: 'sphere' },
      { key: 'odCylinder', label: 'cilíndrico', kind: 'cylinder' },
      { key: 'odAxis', label: 'eixo', kind: 'axis' },
    ]);
    const oeFields = this.buildPrescriptionSection('olho esquerdo (oe)*', [
      { key: 'oeSphere', label: 'esférico', kind: 'sphere' },
      { key: 'oeCylinder', label: 'cilíndrico', kind: 'cylinder' },
      { key: 'oeAxis', label: 'eixo', kind: 'axis' },
    ]);
    const form = document.createElement('div');
    form.className = 'zrzs-quick-add-prescription__form';
    form.appendChild(odFields.section);
    form.appendChild(oeFields.section);
    page.appendChild(form);

    const fieldError = document.createElement('div');
    fieldError.className = 'zrzs-quick-add-prescription__error';
    fieldError.hidden = true;
    fieldError.innerHTML = `
      <p class="zrzs-quick-add-prescription__error-title">campos vazios ou preenchidos incorretamente</p>
      <p class="zrzs-quick-add-prescription__error-body">por favor, verifique os valores inseridos nos campos destacados antes de prosseguir.</p>
    `;

    // Erro de campo (clicou em "continuar" incompleto) e aviso de faixa de
    // grau podem aparecer juntos — um em cima do outro, nessa div, na
    // ordem de quem apareceu primeiro (ver setFieldErrorVisible/
    // setWarningVisible mais abaixo), nunca em ordem fixa. Evita layout
    // shift: os dois continuam sempre no DOM, só alternando hidden.
    const messages = document.createElement('div');
    messages.className = 'zrzs-quick-add-prescription__messages';
    messages.appendChild(fieldError);
    messages.appendChild(warning);
    page.appendChild(messages);

    const disclaimer = document.createElement('p');
    disclaimer.className = 'zrzs-quick-add-prescription__disclaimer';
    disclaimer.textContent =
      'seus dados são usados exclusivamente para configurar suas lentes.';
    page.appendChild(disclaimer);

    /** @type {Record<string, HTMLInputElement | HTMLSelectElement>} */
    const inputs = { ...odFields.inputs, ...oeFields.inputs };
    const requiredKeys = Object.keys(inputs);

    // Só reordena (manda pro fim do container) na transição oculto→visível
    // — um texto atualizado com o elemento já visível não deve "furar a
    // fila" na frente do outro erro, senão a ordem trocaria sozinha sem o
    // usuário ter feito nada.
    let fieldErrorVisible = false;
    let warningVisible = false;
    const setFieldErrorVisible = (/** @type {boolean} */ visible) => {
      if (visible && !fieldErrorVisible) messages.appendChild(fieldError);
      fieldErrorVisible = visible;
      fieldError.hidden = !visible;
    };
    const setWarningVisible = (/** @type {boolean} */ visible) => {
      if (visible && !warningVisible) messages.appendChild(warning);
      warningVisible = visible;
      warning.hidden = !visible;
    };

    // buildPrescriptionStep é reconstruída do zero toda vez que se volta
    // pra cá (ex.: trocar a proteção extra na página 3 refaz essa etapa,
    // já que o preço/faixa pode ter mudado) — sem repopular, a receita
    // digitada sumia silenciosamente (bug real relatado em QA). Prefere o
    // rascunho (draftPrescriptionValues, atualizado a cada tecla, cobre
    // também quem nunca chegou a clicar "confirmar valores") e cai pro
    // confirmado só se não houver rascunho nenhum.
    const valuesToRestore =
      this.draftPrescriptionValues || this.prescriptionValues;
    if (valuesToRestore) {
      const saved = /** @type {Record<string, string>} */ (valuesToRestore);
      requiredKeys.forEach(key => {
        const input = inputs[key];
        if (input) input.value = saved[key] || '';
      });
    }

    const getValues = () => ({
      odSphere: inputs.odSphere.value,
      odCylinder: inputs.odCylinder.value,
      odAxis: inputs.odAxis.value,
      oeSphere: inputs.oeSphere.value,
      oeCylinder: inputs.oeCylinder.value,
      oeAxis: inputs.oeAxis.value,
    });

    /** Eixo depende do cilíndrico do MESMO olho (odAxis↔odCylinder, oeAxis↔oeCylinder). @param {HTMLInputElement | HTMLSelectElement} input */
    const getPairedCylinder = input => {
      if (input === inputs.odAxis) return inputs.odCylinder;
      if (input === inputs.oeAxis) return inputs.oeCylinder;
      return null;
    };

    /** @param {HTMLInputElement | HTMLSelectElement} input */
    const isInputValid = input => {
      if (input.dataset.kind === 'sphere') {
        return isValidDecimalValue(input.value);
      }
      if (input.dataset.kind === 'cylinder') {
        return isValidOptionalDecimalValue(input.value);
      }
      const pairedCylinder = getPairedCylinder(input);
      return isValidAxisValueFor(
        input.value,
        pairedCylinder ? pairedCylinder.value : ''
      );
    };

    // Agrega os campos já marcados em vermelho, não recalcula validade.
    const refreshErrorBanner = () => {
      const hasError = requiredKeys.some(key =>
        inputs[key].classList.contains(
          'zrzs-quick-add-prescription__input--error'
        )
      );
      setFieldErrorVisible(hasError);
    };

    /**
     * No blur/ao digitar, vazio nunca acende vermelho (campo ainda não
     * preenchido não é "erro", só falta preencher) — digitar "-" também não
     * pode acender no meio da digitação. Ao tentar prosseguir (`strict`),
     * obrigatório vazio TAMBÉM conta como erro — o botão nunca fica
     * desabilitado, então é assim que o usuário descobre o que falta.
     * @param {HTMLInputElement | HTMLSelectElement} input
     * @param {boolean} [strict]
     * @returns {boolean} true se válido
     */
    const validateFieldOnBlur = (input, strict = false) => {
      const invalid = strict
        ? !isInputValid(input)
        : input.value.trim() !== '' && !isInputValid(input);
      input.classList.toggle(
        'zrzs-quick-add-prescription__input--error',
        invalid
      );
      refreshErrorBanner();
      return !invalid;
    };

    const warningLink = warning.querySelector('[data-warning-link]');

    // Faixa desconhecida (não cadastrada): não arrisca, não mostra nada.
    const updateWarning = () => {
      const values = getValues();
      const messageEl = warning.querySelector('[data-warning-message]');

      // warningKind (+ required*) fica preso no dataset entre chamadas —
      // sem limpar aqui, corrigir um valor inválido pra um válido mantinha
      // o popup de bloqueio/upgrade disparando em "confirmar valores".
      const clearWarningState = () => {
        setWarningVisible(false);
        delete warning.dataset.warningKind;
        delete warning.dataset.requiredTitle;
        delete warning.dataset.requiredGradingIndex;
      };

      if (!hasAnyGradingValue(values) || !this.lensPages?.page2) {
        clearWarningState();
        return;
      }

      // Via recomendação ainda não tem faixa selecionada pra comparar —
      // só existe "cobre alguma faixa" ou "não cobre nenhuma" (sem opção
      // de upgrade, que pressupõe uma seleção atual pra trocar).
      if (viaRecommend) {
        if (findRequiredGradingOption(this.lensPages.page2, values)) {
          clearWarningState();
          return;
        }
        setWarningVisible(true);
        messageEl.textContent = fitsAnyKnownGradingRange(values)
          ? 'o grau escolhido está fora da faixa que atendemos para esse produto.'
          : 'o grau escolhido está fora da faixa que atendemos.';
        warning.dataset.warningKind = 'exceeds-max';
        delete warning.dataset.requiredTitle;
        delete warning.dataset.requiredGradingIndex;
        return;
      }

      const currentRange = getGradingRangeForTitle(
        this.selectedGradingTitle || ''
      );
      if (!currentRange || valuesFitRange(values, currentRange)) {
        clearWarningState();
        return;
      }

      setWarningVisible(true);

      // Se outra faixa real cobre os valores, oferece upgrade; senão avisa que excede.
      const required = findRequiredGradingOption(this.lensPages.page2, values);
      if (required && required.title !== this.selectedGradingTitle) {
        messageEl.textContent =
          'atenção: grau acima da faixa selecionada identificado';
        warning.dataset.warningKind = 'upgrade';
        warning.dataset.requiredTitle = required.title;
        warning.dataset.requiredGradingIndex = required.gradingIndex || '';
      } else {
        messageEl.textContent = fitsAnyKnownGradingRange(values)
          ? 'o grau escolhido está fora da faixa que atendemos para esse produto.'
          : 'o grau escolhido está fora da faixa que atendemos.';
        warning.dataset.warningKind = 'exceeds-max';
        delete warning.dataset.requiredTitle;
        delete warning.dataset.requiredGradingIndex;
      }
    };

    Object.values(inputs).forEach(input => {
      const handleChange = () => {
        // Dropdown (esférico/cilíndrico): valor já vem certo, nunca precisa
        // sanitizar — só o eixo continua texto livre.
        if (input.tagName !== 'SELECT') {
          input.value =
            input.dataset.kind === 'axis'
              ? sanitizeAxisInput(input.value)
              : sanitizeDecimalInput(input.value);
        }
        updateWarning();
        // Guarda o que está digitado (válido ou não, confirmado ou não) —
        // sem isso, reabrir essa etapa sem nunca ter clicado "confirmar
        // valores" perdia tudo (bug real relatado em QA).
        this.draftPrescriptionValues = getValues();
        // Limpa erro otimisticamente ao digitar, sem esperar o blur.
        input.classList.remove('zrzs-quick-add-prescription__input--error');
        refreshErrorBanner();
      };
      input.addEventListener('input', handleChange);
      // <select> dispara 'input' nos browsers atuais, mas 'change' é o
      // evento canônico dele — mantém os dois por segurança, idempotente.
      if (input.tagName === 'SELECT') {
        input.addEventListener('change', handleChange);
      }
      input.addEventListener('blur', () => validateFieldOnBlur(input));
    });

    // Nenhuma faixa cobre o grau digitado — mesma saída do multifocal
    // (avaliação só é possível presencialmente): fecha o popup e leva pro
    // fluxo de geolocalização/lojas próximas (buildLocationPermissionStep).
    const showExceedsMaxPopup = () => {
      this.showPopup({
        title: 'grau fora da faixa que atendemos',
        body: 'não conseguimos configurar esse grau pra esse produto por aqui. vamos te ajudar a encontrar uma zerezes perto de você pra avaliar as melhores opções.',
        onConfirm: () => {
          this.hidePopup();
          this.pushStep(this.buildLocationPermissionStep());
          // Só nesse entrypoint (fora da faixa) a tela de geolocalização
          // vem com a mesma animação de abertura do carrinho — pedido
          // explícito de UX. Clicar em "multifocal" na página 1 continua
          // chegando ali por navegação normal, sem isso (ver advanceFromPage1).
          this.onReopenAnimated();
        },
      });
    };

    // Avança a etapa quando não há upgrade pendente (usado após confirmar direto ou depois do popup trocar a lente).
    const proceedAfterPrescription = () => {
      if (viaRecommend) {
        if (!this.applyGradingHighlightFromValues()) {
          showExceedsMaxPopup();
          return;
        }
        this.pushStep(this.buildPage2Step());
        return;
      }
      if (this.batchQueue) {
        this.finishCurrentBatchFrame();
        return;
      }
      this.pushStep(this.buildSummaryStep());
    };

    /**
     * @param {string} bodyText
     * @param {boolean} [dismissOnly] "saiba mais" só informa — o popup
     *   fecha igual ao X/voltar, sem aplicar o upgrade nem avançar a
     *   etapa. Só "confirmar valores" (onContinue abaixo) faz isso de
     *   verdade, sem o parâmetro (default false).
     */
    const showUpgradePopup = (bodyText, dismissOnly = false) => {
      const requiredTitle = warning.dataset.requiredTitle;
      const requiredGradingIndex = warning.dataset.requiredGradingIndex;
      if (!requiredTitle || !requiredGradingIndex) return;
      this.showPopup({
        // Não é o grau da pessoa que muda — ele é o mesmo. O que muda é o
        // valor da lente (faixa de grau inclusa na armação é diferente da
        // que a receita exige).
        title: dismissOnly
          ? 'o valor da sua lente será atualizado'
          : 'o valor da sua lente foi atualizado',
        body: bodyText,
        onConfirm: dismissOnly
          ? () => this.hidePopup()
          : () => {
              this.applyRequiredGradingUpgrade(requiredGradingIndex);
              // applyRequiredGradingUpgrade troca selectedGradingTitle direto
              // (não passa por advanceFromPage2) — ressincroniza aqui, senão
              // a receita que acabou de ser validada pra essa faixa nova
              // seria invalidada na próxima vez que a página 2 for revisitada.
              this.prescriptionValuesGradingTitle = this.selectedGradingTitle;
              updateWarning();
              this.hidePopup();
              proceedAfterPrescription();
            },
      });
    };

    warningLink?.addEventListener('click', () => {
      if (warning.dataset.warningKind === 'exceeds-max') {
        showExceedsMaxPopup();
        return;
      }
      showUpgradePopup(
        'com base na sua receita, identificamos que sua lente vai precisar de uma faixa de grau diferente da inclusa na armação. por isso, o valor será ajustado de acordo com o grau da sua receita quando você confirmar e prosseguir.',
        true
      );
    });

    return {
      page,
      title: 'coloque sua receita',
      subtitle: 'preencha os dados da sua receita para continuarmos.',
      custom: true,
      lightContent: true,
      // Via atalho de recomendação: sem skip, para garantir uma lente selecionada.
      showSkip: !viaRecommend,
      continueLabel: 'confirmar valores',
      onRender: () => {
        // Resync de faixa pós-upgrade já roda em goBack() (antes de
        // qualquer render, inclusive quando essa etapa é pulada por
        // advanceFromPage3) — ver resyncGradingFromConfirmedPrescription.
        // Botão nunca fica cinza (pedido de UX) — clicar incompleto mostra
        // o que falta em vez de travar o clique (ver onContinue).
        this.setContinueEnabled(true);
        updateWarning();
        requiredKeys.forEach(key => validateFieldOnBlur(inputs[key]));
      },
      onContinue: () => {
        // Botão sempre habilitado — valida aqui, não antes. Diferente do
        // blur (que ignora campo vazio), aqui vazio TAMBÉM é erro: é o
        // único jeito do usuário descobrir o que falta preencher, já que
        // não há mais estado "desabilitado" indicando isso visualmente.
        const allValid = requiredKeys
          .map(key => validateFieldOnBlur(inputs[key], true))
          .every(Boolean);
        if (!allValid) return;

        this.prescriptionValues = getValues();
        // Atrela a receita confirmada à faixa vigente AGORA — null quando
        // ainda não há faixa escolhida (ex.: veio de "indique minha lente
        // pela receita", antes de clicar na faixa sugerida); ver
        // advanceFromPage2 pra como isso decide invalidar ou não depois.
        this.prescriptionValuesGradingTitle = this.selectedGradingTitle;
        // Mesma checagem do link "saiba mais" — sem isso, confirmar direto
        // (sem clicar em "saiba mais" antes) pulava o aviso de limite e
        // deixava a receita fora de faixa entrar no pedido sem aviso.
        if (warning.dataset.warningKind === 'exceeds-max') {
          showExceedsMaxPopup();
          return;
        }
        if (warning.dataset.warningKind === 'upgrade') {
          showUpgradePopup(
            'com base na sua receita, identificamos que sua lente precisa de uma faixa de grau diferente da inclusa na armação. por isso, o valor foi ajustado de acordo com o grau da sua receita.'
          );
          return;
        }
        proceedAfterPrescription();
      },
      onSkip: () => {
        this.prescriptionValues = null;
        this.prescriptionValuesGradingTitle = null;
        if (this.batchQueue) {
          this.finishCurrentBatchFrame();
          return;
        }
        this.pushStep(this.buildSummaryStep());
      },
    };
  }

  /**
   * Etapa final: breakdown de preço (só linhas > 0) + receita +
   * "adicionar ao carrinho" (roda o /cart/add.js real).
   * @returns {LensFlowStep}
   */
  buildSummaryStep() {
    const page = document.createElement('div');
    page.className = 'lens-selection__page zrzs-quick-add-summary';

    const breakdown = document.createElement('div');
    breakdown.className = 'zrzs-quick-add-summary__breakdown';
    page.appendChild(breakdown);

    if (this.productSummary) {
      breakdown.appendChild(
        this.buildSummaryLine(
          this.productSummary.title || '',
          this.productSummary.price || ''
        )
      );
    }
    if (this.selectedGradingPriceCents > 0) {
      breakdown.appendChild(
        this.buildSummaryLine(
          this.selectedGradingTitle || '',
          formatMoney(this.selectedGradingPriceCents)
        )
      );
    }
    if (this.selectedCoating && this.selectedCoating.priceCents > 0) {
      breakdown.appendChild(
        this.buildSummaryLine(
          this.selectedCoating.title || '',
          formatMoney(this.selectedCoating.priceCents)
        )
      );
    }

    if (this.prescriptionValues) {
      const label = document.createElement('p');
      label.className = 'zrzs-quick-add-summary__receita-label';
      label.textContent = 'sua receita:';
      breakdown.appendChild(label);

      const od = document.createElement('p');
      od.className = 'zrzs-quick-add-summary__receita-line';
      od.textContent = formatPrescriptionSummaryLine(
        this.prescriptionValues,
        'od'
      );
      breakdown.appendChild(od);

      const oe = document.createElement('p');
      oe.className = 'zrzs-quick-add-summary__receita-line';
      oe.textContent = formatPrescriptionSummaryLine(
        this.prescriptionValues,
        'oe'
      );
      breakdown.appendChild(oe);
    }

    const divider = document.createElement('div');
    divider.className = 'zrzs-quick-add-summary__divider';
    breakdown.appendChild(divider);

    const totals = document.createElement('div');
    totals.className = 'zrzs-quick-add-summary__totals';
    breakdown.appendChild(totals);

    const frameCents = this.productSummary?.priceCents || 0;
    // Preço ABSOLUTO da lente final (o que realmente vai pro /cart/add.js)
    // — não a reconstrução "faixa + delta do coating". Garante que esse
    // total bate com o que o carrinho cobra de verdade mesmo se uma opção
    // de proteção extra for cadastrada com preço menor que a própria faixa
    // de grau (o delta zeraria via Math.max em toCoatingDelta, mas o preço
    // absoluto nunca mente).
    const lensCents = this.finalLensSelection
      ? this.finalLensSelection.priceCents
      : this.selectedGradingPriceCents +
        (this.selectedCoating?.priceCents || 0);
    const totalCents = frameCents + lensCents;
    const totalRow = this.buildSummaryRow(
      'total do pedido',
      formatMoney(totalCents)
    );
    totalRow.classList.add('zrzs-quick-add-summary__row--total');
    totals.appendChild(totalRow);

    if (this.selectedGradingIndex) {
      const disclaimer = document.createElement('p');
      disclaimer.className = 'zrzs-quick-add-summary__disclaimer';
      disclaimer.textContent =
        'após o pagamento, confirmaremos sua receita antes da produção.';
      page.appendChild(disclaimer);
    }

    // Falha no /cart/add.js só ia pro console — cliente ficava sem
    // nenhum feedback visível de que "adicionar ao carrinho" não funcionou.
    const errorMessage = document.createElement('p');
    errorMessage.className = 'zrzs-quick-add-summary__error';
    errorMessage.hidden = true;
    page.appendChild(errorMessage);
    this.summaryErrorEl = errorMessage;

    return {
      page,
      title: 'resumo da configuração',
      // selectedGradingIndex (não prescriptionValues) distingue os dois
      // casos: ele é setado assim que uma faixa de grau é escolhida em
      // page2, mesmo que a receita seja enviada depois — prescriptionValues
      // só existe depois de preenchida, o que apagava esse texto também em
      // "enviar depois" (bug relatado). Sem grading nenhum = "filtro de luz
      // azul" (única opção sem grau que chega aqui — "apenas armação" nunca
      // passa pelo resumo), aí não faz sentido falar de "lentes".
      subtitle: this.selectedGradingIndex
        ? 'confira as escolhas das suas lentes antes de finalizar.'
        : 'confira o seu pedido antes de finalizar.',
      custom: true,
      lightContent: true,
      continueLabel: 'adicionar ao carrinho',
      onRender: () => this.setContinueEnabled(true),
      onContinue: () => this.handleAddToCartFromSummary(),
    };
  }

  /**
   * Resumo do fluxo em lote (grau retroativo em N armações solares já no
   * carrinho) — uma seção por armação configurada nesta passada. O total
   * soma armação + lentes de cada uma (mesmo a armação já estando na
   * compra original — reportado como confuso mostrar só o valor da
   * lente no "total do pedido").
   * @returns {LensFlowStep}
   */
  buildBatchSummaryStep() {
    const page = document.createElement('div');
    page.className = 'lens-selection__page zrzs-quick-add-summary';

    const breakdown = document.createElement('div');
    breakdown.className = 'zrzs-quick-add-summary__breakdown';
    page.appendChild(breakdown);

    let totalCents = 0;
    let hasAnyPrescription = false;
    this.batchResults.forEach((result, index) => {
      if (index > 0) {
        const divider = document.createElement('div');
        divider.className = 'zrzs-quick-add-summary__divider';
        breakdown.appendChild(divider);
      }

      const frameLabel = document.createElement('p');
      frameLabel.className = 'zrzs-quick-add-summary__frame-label';
      frameLabel.textContent = result.productSummary?.title || '';
      breakdown.appendChild(frameLabel);

      const frameCents = result.productSummary?.priceCents || 0;
      totalCents += frameCents;
      if (frameCents > 0) {
        breakdown.appendChild(
          this.buildSummaryLine('armação', formatMoney(frameCents))
        );
      }

      const lensCents = result.lens?.priceCents || 0;
      totalCents += lensCents;
      if (lensCents > 0) {
        breakdown.appendChild(
          this.buildSummaryLine('lentes + acabamento', formatMoney(lensCents))
        );
      }

      if (result.prescription) {
        hasAnyPrescription = true;
        const label = document.createElement('p');
        label.className = 'zrzs-quick-add-summary__receita-label';
        label.textContent = 'sua receita:';
        breakdown.appendChild(label);

        const od = document.createElement('p');
        od.className = 'zrzs-quick-add-summary__receita-line';
        od.textContent = formatPrescriptionSummaryLine(
          result.prescription,
          'od'
        );
        breakdown.appendChild(od);

        const oe = document.createElement('p');
        oe.className = 'zrzs-quick-add-summary__receita-line';
        oe.textContent = formatPrescriptionSummaryLine(
          result.prescription,
          'oe'
        );
        breakdown.appendChild(oe);
      }
    });

    const totalsDivider = document.createElement('div');
    totalsDivider.className = 'zrzs-quick-add-summary__divider';
    breakdown.appendChild(totalsDivider);

    const totals = document.createElement('div');
    totals.className = 'zrzs-quick-add-summary__totals';
    breakdown.appendChild(totals);

    const totalRow = this.buildSummaryRow(
      'total do pedido',
      formatMoney(totalCents)
    );
    totalRow.classList.add('zrzs-quick-add-summary__row--total');
    totals.appendChild(totalRow);

    if (hasAnyPrescription) {
      const disclaimer = document.createElement('p');
      disclaimer.className = 'zrzs-quick-add-summary__disclaimer';
      disclaimer.textContent =
        'após a confirmação, seguiremos com a produção das lentes.';
      page.appendChild(disclaimer);
    }

    const errorMessage = document.createElement('p');
    errorMessage.className = 'zrzs-quick-add-summary__error';
    errorMessage.hidden = true;
    page.appendChild(errorMessage);
    this.summaryErrorEl = errorMessage;

    return {
      page,
      title: 'resumo da configuração',
      subtitle: 'confira as escolhas das suas lentes antes de finalizar.',
      custom: true,
      lightContent: true,
      continueLabel: 'adicionar ao carrinho',
      onRender: () => this.setContinueEnabled(true),
      onContinue: () => this.handleBatchAddToCart(),
    };
  }

  /** "adicionar ao carrinho" do resumo em lote — a mutação real do carrinho é do chamador (overlay). */
  async handleBatchAddToCart() {
    if (!this.batchOnComplete) return;
    this.setContinueEnabled(false);
    this.setSummaryError('');
    try {
      const ok = await this.batchOnComplete(this.batchResults);
      if (ok === false) {
        this.setSummaryError(
          'não foi possível salvar as configurações agora. tenta de novo.'
        );
        return;
      }
    } finally {
      this.setContinueEnabled(true);
    }
  }

  /**
   * Linha de preço: label lowercase à esquerda, valor em negrito à direita.
   * @param {string} label
   * @param {string} value
   */
  buildSummaryLine(label, value) {
    return buildLabelValueRow(label, value, {
      row: 'zrzs-quick-add-summary__line',
      label: 'zrzs-quick-add-summary__line-label',
      value: 'zrzs-quick-add-summary__line-value',
    });
  }

  /**
   * Linha prazo/total — só o label é lowercase (valor pode ser preço).
   * @param {string} label
   * @param {string} value
   */
  buildSummaryRow(label, value) {
    return buildLabelValueRow(label, value, {
      row: 'zrzs-quick-add-summary__row',
      label: 'zrzs-quick-add-summary__row-label',
      value: 'zrzs-quick-add-summary__row-value',
    });
  }

  /** Roda o /cart/add.js real do fluxo de grau. */
  async handleAddToCartFromSummary() {
    if (!this.finalLensSelection || !this.onAddConfiguredLensToCart) return;
    this.setContinueEnabled(false);
    this.setSummaryError('');
    try {
      const added = await this.onAddConfiguredLensToCart({
        lens: this.finalLensSelection,
        prescription: this.prescriptionValues,
      });
      if (added === false) {
        this.setSummaryError(
          'não foi possível adicionar ao carrinho agora. tenta de novo.'
        );
      }
    } finally {
      this.setContinueEnabled(true);
    }
  }

  /** @param {string} message string vazia esconde. */
  setSummaryError(message) {
    if (!this.summaryErrorEl) return;
    this.summaryErrorEl.textContent = message;
    this.summaryErrorEl.hidden = !message;
  }

  /**
   * Esférico/cilíndrico: dropdown com as faixas já definidas (passo de
   * 0,25) — nunca deixa digitar um valor fora de passo. Eixo continua
   * texto livre (0-180°, sem faixa discreta cadastrada).
   * @param {string} title
   * @param {Array<{ key: string, label: string, kind: 'sphere' | 'cylinder' | 'axis' }>} fields
   * @returns {{ section: HTMLElement, inputs: Record<string, HTMLInputElement | HTMLSelectElement> }}
   */
  buildPrescriptionSection(title, fields) {
    const section = document.createElement('div');
    section.className = 'zrzs-quick-add-prescription__section';

    const heading = document.createElement('p');
    heading.className = 'zrzs-quick-add-prescription__section-title';
    heading.textContent = title;
    section.appendChild(heading);

    const row = document.createElement('div');
    row.className = 'zrzs-quick-add-prescription__row';
    section.appendChild(row);

    /** @type {Record<string, HTMLInputElement | HTMLSelectElement>} */
    const inputs = {};

    fields.forEach(field => {
      const fieldWrapper = document.createElement('div');
      fieldWrapper.className = 'zrzs-quick-add-prescription__field';

      const label = document.createElement('p');
      label.className = 'zrzs-quick-add-prescription__field-label';
      label.textContent = field.label;
      fieldWrapper.appendChild(label);

      /** @type {HTMLInputElement | HTMLSelectElement} */
      let input;
      if (field.kind === 'axis') {
        input = document.createElement('input');
        input.type = 'text';
        input.inputMode = 'numeric';
        input.autocomplete = 'off';
      } else {
        const select = document.createElement('select');
        const placeholder = document.createElement('option');
        placeholder.value = '';
        // Esférico é obrigatório (nunca mais selecionável depois de
        // escolher um valor real) — cilíndrico fica opcional (vazio =
        // sem astigmatismo, uma opção de verdade, não só um prompt).
        if (field.kind === 'sphere') {
          placeholder.textContent = 'selecione';
          placeholder.disabled = true;
          placeholder.hidden = true;
        } else {
          placeholder.textContent = 'nenhum';
        }
        placeholder.selected = true;
        select.appendChild(placeholder);

        const stepOptions =
          field.kind === 'sphere' ? SPHERE_STEP_OPTIONS : CYLINDER_STEP_OPTIONS;
        stepOptions.forEach(value => {
          const option = document.createElement('option');
          option.value = value;
          option.textContent = value;
          select.appendChild(option);
        });
        input = select;
      }
      input.className = 'zrzs-quick-add-prescription__input';
      input.dataset.kind = field.kind;
      input.setAttribute('aria-label', field.label);
      fieldWrapper.appendChild(input);

      inputs[field.key] = input;
      row.appendChild(fieldWrapper);
    });

    return { section, inputs };
  }

  /**
   * @param {{ title: string, body: string, onConfirm: () => void }} config
   */
  showPopup({ title, body, onConfirm }) {
    const popup = this.refs.popup;
    if (!popup?.root) return;
    if (popup.title) popup.title.textContent = title;
    if (popup.body) popup.body.textContent = body;
    if (popup.continueButton) {
      if (this.popupConfirmHandler) {
        popup.continueButton.removeEventListener(
          'click',
          this.popupConfirmHandler
        );
      }
      // Duplo clique rápido disparava onConfirm 2x (avançava o histórico
      // duas vezes) — só a 1ª chamada por exibição do popup tem efeito.
      let handled = false;
      this.popupConfirmHandler = () => {
        if (handled) return;
        handled = true;
        onConfirm();
      };
      popup.continueButton.addEventListener('click', this.popupConfirmHandler);
    }
    popup.root.hidden = false;
  }

  hidePopup() {
    const popup = this.refs.popup;
    if (popup?.root) popup.root.hidden = true;
  }

  /**
   * Cópia rasa dos campos de progresso, presa a cada etapa do histórico
   * (pushStep/goBack) para restaurar o estado real ao voltar.
   * prescriptionValues/prescriptionValuesGradingTitle ficam DE FORA de
   * propósito — precisam sobreviver ao voltar (ver onde são declarados).
   * @returns {object}
   */
  captureSelectionState() {
    return {
      selectedGradingIndex: this.selectedGradingIndex,
      selectedGradingTitle: this.selectedGradingTitle,
      selectedGradingPriceCents: this.selectedGradingPriceCents,
      selectedCoating: this.selectedCoating,
      finalLensSelection: this.finalLensSelection,
      stepTotalOverride: this.stepTotalOverride,
      recommendUsedInThisPass: this.recommendUsedInThisPass,
      willFillPrescriptionNow: this.willFillPrescriptionNow,
      highlightedGradingIndex: this.highlightedGradingIndex,
      lensPages: this.lensPages,
      batchIndex: this.batchIndex,
      batchResults: this.batchQueue
        ? [...this.batchResults]
        : this.batchResults,
    };
  }

  /**
   * Enquanto open()/advanceBatchFrame() buscam a página do produto — sem
   * isso uma resposta lenta parecia travada (painel só trocava depois do
   * fetch). Renderiza direto, sem empilhar em history.
   */
  showLoadingSkeleton() {
    if (!this.refs.content) return;
    const page = document.createElement('div');
    page.className = 'lens-selection__page zrzs-quick-add-lens-skeleton';
    for (let i = 0; i < 3; i++) {
      const card = document.createElement('div');
      card.className = 'zrzs-quick-add-lens-skeleton__card';
      const title = document.createElement('span');
      title.className =
        'zrzs-quick-add-lens-skeleton__line zrzs-quick-add-lens-skeleton__line--title';
      const desc = document.createElement('span');
      desc.className =
        'zrzs-quick-add-lens-skeleton__line zrzs-quick-add-lens-skeleton__line--desc';
      card.append(title, desc);
      page.appendChild(card);
    }
    this.render({
      page,
      title: 'configurar lentes',
      subtitle: 'como você vai usar seus óculos?',
      custom: true,
      hideStepCounter: true,
      onRender: () => this.setContinueEnabled(false),
    });
    // Visível e clicável desde já (sem isso, aparecer só quando a página 1
    // carrega empurrava o título do lado — layout shift reportado). Clicar
    // aqui durante o loading cai no ramo "história vazia" de goBack(), que
    // já invalida o fetch em voo antes de sair — seguro.
    if (this.refs.backButton) this.refs.backButton.hidden = false;
    if (this.refs.skipButton) this.refs.skipButton.hidden = true;
  }

  /**
   * @param {LensFlowStep} step
   */
  pushStep(step) {
    step.stateSnapshot = this.captureSelectionState();
    this.history.push(step);
    this.render(step);
  }

  /**
   * Reexibe a etapa atual sem refazer fetch nem alterar o histórico —
   * usado quando o overlay reabre (mesma armação) depois de ter sido
   * fechado no meio do fluxo (ver
   * zrzs-quick-add-overlay.js#handlePendingOpen, condição de "resumir").
   * @returns {boolean} true se havia uma etapa pra reexibir
   */
  resumeCurrentStep() {
    const current = this.history[this.history.length - 1];
    if (!current) return false;
    this.render(current);
    return true;
  }

  goBack() {
    this.history.pop();
    const previous = this.history[this.history.length - 1];
    if (previous) {
      if (previous.stateSnapshot) Object.assign(this, previous.stateSnapshot);
      this.resyncGradingFromConfirmedPrescription();
      this.render(previous);
    } else {
      // Sem histórico pra voltar (inclusive clicando aqui ainda durante o
      // loading, história sempre vazia nesse momento) — invalida um
      // open()/advanceBatchFrame() em voo antes de sair, senão o fetch
      // resolve depois e empurra a página 1 por cima do painel do onExit.
      this.invalidatePendingOpen();
      this.onExit();
    }
  }

  /** @param {LensFlowStep} step */
  render(step) {
    const { refs } = this;
    // Toda troca de etapa destrava o clique da etapa anterior (ver
    // wireSelectableOption/oneClickLock) — só fica travado mesmo enquanto
    // a etapa ATUAL segue em tela (ex.: /cart/add.js direto sem navegar).
    this.oneClickLock = false;
    // Etapa de permissão não tem título/subtítulo — sheet em tela cheia,
    // sem backdrop clicável. Mostra só ⊗ e ← (ver refs.backButton abaixo).
    if (refs.title) {
      refs.title.textContent = step.title;
      refs.title.hidden = Boolean(step.hideHeaderChrome);
    }
    if (refs.subtitle) {
      refs.subtitle.textContent = step.subtitle;
      refs.subtitle.hidden = Boolean(step.hideHeaderChrome);
    }
    if (refs.header) {
      refs.header.classList.toggle(
        'zrzs-quick-add-overlay__lens-header--bare',
        Boolean(step.hideHeaderChrome)
      );
      refs.header.classList.toggle(
        'zrzs-quick-add-overlay__lens-header--light',
        Boolean(step.lightContent)
      );
    }
    if (refs.stepCounter) {
      if (step.hideStepCounter) {
        refs.stepCounter.hidden = true;
      } else {
        refs.stepCounter.textContent = `etapa ${this.history.length - this.stepCounterBase} de ${this.getStepTotal()}`;
        refs.stepCounter.hidden = false;
      }
    }
    if (refs.backButton) {
      // Independente de hideHeaderChrome (título/subtítulo/contador) — a
      // etapa de permissão de localização usa hideHeaderChrome mas agora
      // também mostra o botão de voltar, mesmo ícone/tamanho/posição das
      // outras etapas (alinhado ao ⊗ via margin-left:auto do ⊗, ver CSS).
      refs.backButton.hidden = Boolean(step.hideBackButton);
    }
    if (refs.skipButton) refs.skipButton.hidden = !step.showSkip;
    if (refs.continueButton) {
      refs.continueButton.textContent = step.continueLabel || 'continuar';
    }
    if (refs.footer) {
      refs.footer.classList.toggle(
        'zrzs-quick-add-overlay__lens-footer--light',
        Boolean(step.custom)
      );
      // Etapa de opções (não custom) sem requireContinueButton: variante sem
      // botão, some o rodapé (avança no clique da própria opção — ver
      // wireSelectableOption). Custom (receita/resumo/etc.) e
      // requireContinueButton: true continuam só no hideFooter de sempre.
      refs.footer.hidden =
        Boolean(step.hideFooter) ||
        (!step.custom && !step.requireContinueButton);
    }

    if (refs.content) {
      refs.content.classList.toggle(
        'zrzs-quick-add-overlay__lens-content--light',
        Boolean(step.lightContent)
      );
      refs.content.innerHTML = '';
      refs.content.appendChild(step.page);
      if (step.footnote) {
        const footnote = document.createElement('p');
        footnote.className = 'zrzs-quick-add-overlay__lens-footnote';
        footnote.textContent = step.footnote;
        refs.content.appendChild(footnote);
      }
    }

    // Etapa custom decide sozinha o estado do "continuar" via onRender/onContinue.
    if (step.custom) {
      this.pendingSelection = null;
      if (step.onRender) step.onRender();
      this.onOpen();
      return;
    }

    this.wireSelectableOptions(step);

    if (step.rememberSelection === false) {
      // Limpa seleção residual de uma visita anterior à página cacheada.
      step.page
        .querySelectorAll('.lens-selection__option.is-selected')
        .forEach(option => option.classList.remove('is-selected'));
    }

    // Restaura seleção ainda marcada no DOM da página cacheada; ignora opção oculta.
    const selected = /** @type {HTMLElement | null} */ (
      step.page.querySelector('.lens-selection__option.is-selected')
    );
    if (selected && selected.style.display !== 'none') {
      this.pendingSelection = { option: selected, onSelect: step.onSelect };
      this.setContinueEnabled(true);
    } else {
      this.pendingSelection = null;
      this.setContinueEnabled(false);
    }

    // Roda depois do restauro genérico acima, para poder sobrescrevê-lo
    // (ver buildPage2Step).
    if (step.onRender) step.onRender();

    this.onOpen();
  }

  /**
   * Total a partir da 1ª etapa VISÍVEL (página 1 nunca mostra contador —
   * ver stepCounterBase): grau + coating (se existirem) + escolha de
   * receita + resumo. Receita só entra na conta quando "preencher receita
   * agora" foi de fato escolhido (willFillPrescriptionNow) — "enviar
   * depois" nunca passa por aquela etapa, então não deve contar.
   * +1 só quando "indique minha lente pela receita" é realmente clicado
   * (recommendUsedInThisPass) — ação deliberada do usuário, não "só seguir
   * em frente", então pode saltar o total nesse instante sem confundir.
   * Nunca menor que a posição atual — se o caminho real for mais longo
   * (bug/edge case não previsto aqui), o total ainda assim cresce em vez
   * de ficar incoerente.
   * @returns {number}
   */
  getStepTotal() {
    const position = this.history.length - this.stepCounterBase;
    if (this.stepTotalOverride != null) {
      return Math.max(this.stepTotalOverride, position);
    }
    let total = 0;
    if (this.lensPages?.page2) total += 1;
    if (this.lensPages?.page3) total += 1;
    total += 1; // como seguir com a receita (buildPrescriptionChoiceStep)
    if (this.willFillPrescriptionNow) total += 1; // receita, só se escolhida
    total += 1; // resumo
    if (this.recommendUsedInThisPass) total += 1;
    return Math.max(total, position);
  }

  /**
   * @param {LensFlowStep} step
   */
  wireSelectableOptions(step) {
    step.page.querySelectorAll('.lens-selection__option').forEach(option => {
      if (!(option instanceof HTMLElement) || option.dataset.inert) return;
      this.wireSelectableOption(option, step);
    });
  }

  /**
   * @param {HTMLElement} option
   * @param {LensFlowStep} step
   */
  wireSelectableOption(option, step) {
    const { page, onSelect } = step;
    // Página 1 (sem preço) ganha indicador de seleção; grau/coating usam só a borda.
    const hasPriceRow = !!option.querySelector(
      '.lens-selection__option-content'
    );
    const button = option.querySelector('.lens-selection__option-button');
    if (
      !hasPriceRow &&
      button &&
      !option.querySelector('.zrzs-quick-add-overlay__lens-option-row')
    ) {
      const row = document.createElement('div');
      row.className = 'zrzs-quick-add-overlay__lens-option-row';
      button.replaceWith(row);
      row.appendChild(button);
      row.appendChild(this.buildIndicatorDot());
    }

    // Página cacheada, render() roda de novo — evita empilhar listeners.
    if (option.dataset.zqaWired) return;
    option.dataset.zqaWired = 'true';

    option.addEventListener('click', () => {
      page.querySelectorAll('.lens-selection__option').forEach(other => {
        other.classList.remove('is-selected');
      });
      option.classList.add('is-selected');
      this.pendingSelection = { option, onSelect };
      this.setContinueEnabled(true);
      // Variante sem botão (default, ver render): o clique na opção já avança,
      // sem esperar "continuar". requireContinueButton: true na etapa volta
      // pro clique em 2 passos (rodapé visível, ver handleContinueClick).
      // oneClickLock evita 2º clique disparar onSelect de novo enquanto a
      // etapa atual não troca (ver comentário no construtor).
      if (!step.requireContinueButton && !this.oneClickLock) {
        this.oneClickLock = true;
        this.handleContinueClick();
      }
    });
  }

  buildIndicatorDot() {
    const dot = document.createElement('span');
    dot.className = 'zrzs-quick-add-overlay__lens-option-dot';
    dot.innerHTML =
      '<svg viewBox="0 0 10 8" fill="none" aria-hidden="true"><path d="M1 4L3.5 6.5L9 1" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    return dot;
  }

  /** @param {boolean} enabled */
  setContinueEnabled(enabled) {
    if (this.refs.continueButton) this.refs.continueButton.disabled = !enabled;
  }

  /**
   * Substitui "etapa N de M" por um texto próprio (ex.: "3 encontradas",
   * "6 lojas") — usado por telas que resolvem a contagem de forma
   * assíncrona.
   * @param {string} text
   */
  setStepCounterText(text) {
    if (!this.refs.stepCounter) return;
    this.refs.stepCounter.textContent = text;
    this.refs.stepCounter.hidden = false;
  }

  handleContinueClick() {
    const current = this.history[this.history.length - 1];
    if (current?.custom) {
      if (current.onContinue) current.onContinue();
      return;
    }
    if (!this.pendingSelection) return;
    const { option, onSelect } = this.pendingSelection;
    onSelect(option);
  }

  /** "enviar depois" — cada etapa decide sozinha o que ele faz. */
  handleSkipClick() {
    const current = this.history[this.history.length - 1];
    if (current?.onSkip) current.onSkip();
  }
}
